import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccountFetch, reportAccountFetchError } from '../hooks/useAccountFetch';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Camera, AlertTriangle, CheckCircle, Clock, LogOut, User,
  MapPin, Fingerprint, Send, Calendar, ArrowRightLeft, Trophy, Users,
  Play, Square, ClipboardList, Truck, ChevronRight, ChevronLeft, Sparkles, Megaphone,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardShell from '../components/DashboardShell';
import { DashboardMenuTabs } from '../components/DashboardMenuTabs';
import { staffDashboardMenu } from '../lib/dashboardMenus';
import { cleanersCcMenuItem } from '../components/sidebar/CleanersCCLink';
import SupervisorSpecialtyQueue from '../components/SupervisorSpecialtyQueue';
import { HierarchyAssignments } from '../components/staff/HierarchyAssignments';
import { RegionalDelegationWorkspace } from '../components/staff/RegionalDelegationWorkspace';
import { SupervisorSharedTeams } from '../components/staff/SharedDelegationCards';
import { SupervisorChainBoards } from '../components/staff/SupervisorChainBoards';
import { CrewLeaderChainBoards } from '../components/staff/CrewLeaderChainBoards';
import { AgentJobCards } from '../components/staff/AgentJobCards';
import { CrewDelegation } from '../components/staff/CrewDelegation';
import { CrewLeaderboard } from '../components/staff/CrewLeaderboard';
import { PropertyStructureBoard } from '../components/staff/PropertyStructureBoard';
import { ActualBagCounts } from '../components/staff/ActualBagCounts';
import { ServiceSignalsLegend } from '../components/ServiceSignalsLegend';
import { UserAvatar } from '../components/UserAvatar';
import { SupervisorRegionalAssignmentCard } from '../components/staff/SupervisorRegionalAssignmentCard';
import { RegionalTeamBoardCard } from '../components/staff/RegionalTeamBoardCard';
import LiveRoutesMap from '../components/admin/LiveRoutesMap';
import AiAccuracyTrendCard from '../components/admin/AiAccuracyTrendCard';
import RouteAlertsPanel from '../components/admin/RouteAlertsPanel';
import AlertConfigCard from '../components/admin/AlertConfigCard';
import { LeaderboardCard } from '../components/admin/LeaderboardCard';
import { RegionalCommandBoard } from '../components/staff/RegionalCommandBoard';
import { RegionalAssignmentCards } from '../components/staff/RegionalAssignmentCards';
import { SupervisorProgressBars } from '../components/staff/SupervisorProgressBars';
import { SupervisorAgentViewPreview } from '../components/staff/SupervisorAgentViewPreview';
import { BuildingScheduler } from '../components/staff/BuildingScheduler';
import { OrderedJobStepperCard } from '../components/staff/OrderedJobStepperCard';
import ServiceSummaryWidget from '../components/staff/ServiceSummaryWidget';
// Iter-170 · Phase 2 — Live Messages panel + unread badge state.
import LiveMessagesPanel from '../components/staff/LiveMessagesPanel';
import FieldAgentStatusBadge from '../components/staff/FieldAgentStatusBadge';
import { RegionalHeatMap } from '../components/staff/RegionalHeatMap';
import { RegionalInventoryTab } from '../components/staff/RegionalInventoryTab';
import { ExecutiveHQ } from '../components/staff/ExecutiveHQ';
import { GoodJobDialog } from '../components/staff/GoodJobDialog';
import { ServiceHistorySummary } from '../components/staff/ServiceHistorySummary';
import { useProfilePhotoSync } from '../hooks/useProfilePhotoSync';
import { DelegatedPickupEntry } from '../components/staff/DelegatedPickupEntry';
import { MyDailyAssignments } from '../components/staff/MyDailyAssignments';
import WelcomeStrip from '../components/WelcomeStrip';
import RegionScopeStrip from '../components/RegionScopeStrip';
import LocationDisclosureModal from '../components/LocationDisclosureModal';
import ContractedServicesCard from '../components/ContractedServicesCard';
import CrewAssignmentsTab from '../components/staff/CrewAssignmentsTab';
import { RouteTaskDialog } from '../components/admin/DeptTaskShared';
import UserMenu from '../components/UserMenu';
import NotificationsBell from '../components/NotificationsBell';
import ComplianceTrophy from '../components/ComplianceTrophy';
import CleanerSpecialtyBoard from '../components/staff/CleanerSpecialtyBoard';
import AttendanceTab from '../components/AttendanceTab';
import PhotoCapture from '../components/PhotoCapture';
import NECSLogo, { NECSQRCode } from '../components/NECSLogo';
import QRScanner from '../components/QRScanner';
import TrainingLauncher from '../components/TrainingLauncher';
import UpdatesNoticesPanel, { useUpdatesUnread } from '../components/updates/UpdatesNoticesPanel';
import UpdatesBlockingGate from '../components/updates/UpdatesBlockingGate';
import LanguageSwitcher from '../components/LanguageSwitcher';
import StaffChatTab from '../components/staff/StaffChatTab';
import StaffReportsTab from '../components/staff/StaffReportsTab';
import EmergencyBroadcastPanel from '../components/admin/EmergencyBroadcastPanel';
import StaffJobControls from '../components/StaffJobControls';
import FleetVehicleDetail from '../components/procurement/FleetVehicleDetail';
import ResidentOperationRecord from './resident/ResidentOperationRecord';
import DriverTrackingPanel from '../components/DriverTrackingPanel';
import { STAFF_SHIFTS as SHIFTS } from '../utils/staffShifts';
import FlagUnitCard from '../components/FlagUnitCard';
import PickupQueueCard from '../components/PickupQueueCard';
// Iter-194 · Field Staff Access Preview — read-only mirror of the
// property's gate/amenity codes so drivers/cleaners see access
// information before dispatch.
import PropertyAccessEntryMirror from '../components/PropertyAccessEntryMirror';
import { KeyRound } from 'lucide-react';
import { useAppIcons } from '../lib/appIcons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAILY_REPORT_CHECKLIST = [
  { key: 'trash_pickup', label: 'Trash pickup completed', section: 'trash' },
  // Iter-170 · Phase 3a — Recycle Pickup as a distinct checklist item.
  { key: 'recycle_pickup', label: 'Recycle pickup completed', section: 'recycle' },
  { key: 'bulk_removal', label: 'Bulk Removal', section: 'bulk' },
  { key: 'power_wash', label: 'Power wash completed', section: 'power_wash' },
  { key: 'amenity_cleaned', label: 'Amenity cleaned', section: 'amenity' },
  { key: 'violations_recorded', label: 'Violations recorded', section: 'violations' },
  { key: 'dumpsters_clean', label: 'Dumpsters clear & clean', section: 'dumpsters' },
  { key: 'common_area_trash_replaced', label: 'Common area trash replaced', section: 'common' }
];

// Iter-170 · Phase 3b — checklist items whose photos load into their
// dedicated section rather than the generic photo upload field.
const DEDICATED_PHOTO_KEYS = new Set(['trash_pickup', 'recycle_pickup', 'common_area_trash_replaced']);

const VEHICLE_CHECKLIST = [
  { key: 'tires', label: 'Tires (Pressure, Tread)' },
  { key: 'windows', label: 'Windows & Windshield' },
  { key: 'fuel', label: 'Fuel Level' },
  { key: 'seats', label: 'Seats & Seatbelts' },
  { key: 'lights_and_signals', label: 'Lights & Signals' },
  { key: 'brakes', label: 'Brakes (Operation)' },
  { key: 'horn', label: 'Horn Operation' },
  { key: 'others', label: 'Other/General Condition' }
];

export default function StaffDashboard() {
  useAppIcons('staff');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, loading, logout } = useAuth();
  const fetch = useAccountFetch(user?.user_id);
  useProfilePhotoSync(setUser);

  // Lead-crew roles share Team Assignments, Uber Trash Ops, Fleet Status,
  // Shift Swap Approvals, live tracking and team contact lists. Site
  // Agents stay focused on their own actions/schedule/reports.
  // Iter-77 (part 9) — CEO / admin / company_manager now use this same
  // dashboard, so include them in the lead-crew umbrella.
  const isLeadRole = ['admin', 'company_manager', 'manager', 'supervisor', 'crew_leader'].includes(user?.role);
  const isRegionalManagerView = ['admin', 'company_manager', 'manager'].includes(user?.role);
  const canAccessExecutiveHQ = ['admin', 'company_manager'].includes(user?.role);

  const [currentLocation, setCurrentLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('actions');
  // Iter-170 · Phase 2 — Live Messages unread count polled from the
  // backend so the sidebar badge stays fresh.
  const [liveMsgUnread, setLiveMsgUnread] = useState(0);
  // Section 4 (iter-134) — Fleet vehicle detail expansion.
  const [fleetExpandedId, setFleetExpandedId] = useState(null);
  const updatesUnread = useUpdatesUnread();
  const [locationPermission, setLocationPermission] = useState('prompt');
  const locationWarningShown = useRef(false);
  // Password and passkey sign-in both create a server-verified session.
  const sessionVerified = Boolean(user);
  const [scheduleRouteCtx, setScheduleRouteCtx] = useState(null);
  const [executiveHQOpen, setExecutiveHQOpen] = useState(false);
  const [goodJobOpen, setGoodJobOpen] = useState(false);

  const [showViolationDialog, setShowViolationDialog] = useState(false);
  const [showLateNotification, setShowLateNotification] = useState(false);
  const [showSwapRequest, setShowSwapRequest] = useState(false);
  const [showDailyReportDialog, setShowDailyReportDialog] = useState(false);
  const [myDailyReports, setMyDailyReports] = useState([]);
  
  const [showSafetyDialog, setShowSafetyDialog] = useState(false);
  const [safetyStep, setSafetyStep] = useState(1);
  const [safetyForm, setSafetyForm] = useState({
    check_type: '',
    vehicle_id: '',
    vehicle_name: '',
    has_license: null,
    license_photo: null,
    fuel_level: '',
    gas_receipt_photo: null,
    checklist: {
      tires: false, windows: false, fuel: false, seats: false,
      lights_and_signals: false, brakes: false, horn: false, others: false
    },
    notes: '',
    vehicle_photos: []
  });

  const [dailyReportForm, setDailyReportForm] = useState({
    report_date: new Date().toISOString().split('T')[0],
    hours_worked: '',
    property_id: '',
    checklist: {
      trash_pickup: false,
      // Iter-170 · Phase 3a — Recycle Pickup checklist state.
      recycle_pickup: false,
      bulk_removal: false,
      power_wash: false,
      amenity_cleaned: false,
      violations_recorded: false,
      dumpsters_clean: false,
      common_area_trash_replaced: false
    },
    // Iter-170 · Phase 3b — dedicated photo fields per service type.
    // Trash / Recycle / Common Area photos load HERE, not into the
    // generic gallery. Everything else lands in `photos`.
    trash_photos: [],
    recycle_photos: [],
    common_area_photos: [],
    photos: [],
    notes: ''
  });
  const [myViolations, setMyViolations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [mySchedules, setMySchedules] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [staffGallery, setStaffGallery] = useState([]);
  const [pickupStats, setPickupStats] = useState({ today: 0, weekly: 0 });
  // Lead-role regional view — Region → Properties → Staff matrix used by
  // Manager / Supervisor / Crew Leader to assign service crews precisely.
  const [regionalTeamView, setRegionalTeamView] = useState([]);
  const [selectedAssignRegion, setSelectedAssignRegion] = useState('');
  const [staffToAssignIds, setStaffToAssignIds] = useState([]);
  // Iter-77 · Set of crew_leader user_ids that have a confirmed
  // crew_assignments doc for today — used to filter the "Today's Daily
  // Job Assignments" section to just the confirmed leaders.
  const [todayCrewLeaderIds, setTodayCrewLeaderIds] = useState(new Set());
  // Today's Service Plan — per-region scheduled vs completed roll-up shown
  // at the morning standup so leads spot at-risk regions in one glance.
  const [servicePlan, setServicePlan] = useState({
    rows: [], overall_scheduled: 0, overall_completed: 0, overall_completion_pct: 0,
    regions_at_risk: [], date: '',
  });
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [todaySchedule, setTodaySchedule] = useState(null);

  // QR scanner state
  const [qrScannerMode, setQrScannerMode] = useState(null); // 'clock-in' | 'pickup' | 'badge'
  const [showPickupForm, setShowPickupForm] = useState(false);
  const [pickupContext, setPickupContext] = useState(null); // { qr_data, property_name, unit_number }
  const [pickupForm, setPickupForm] = useState({ issue_type: 'none', notes: '', photos: [], actual_trash_bags: '', actual_recycling_bags: '', building_number: '' });
  const [showBadgeResult, setShowBadgeResult] = useState(false);
  const [badgeResult, setBadgeResult] = useState(null);

  // QR clock-in approval flow
  const [myClockInRequest, setMyClockInRequest] = useState(null); // { status, property_name, ... }
  const [pendingClockIns, setPendingClockIns] = useState([]); // supervisor / crew_leader queue
  
  const [selectedTrackingProperty, setSelectedTrackingProperty] = useState('');
  // Iter-194 · Field Staff Access Preview — separate property selector
  // (read-only) so field staff can look up gate/amenity codes before
  // heading to a site.
  const [selectedAccessProperty, setSelectedAccessProperty] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  const [violationForm, setViolationForm] = useState({
    property_id: '',
    unit_number: '',
    building_number: '',
    violation_type: '',
    description: '',
    photos: []
  });
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  
  const [lateForm, setLateForm] = useState({
    schedule_id: '',
    reason: '',
    estimated_arrival_time: ''
  });
  
  const [swapForm, setSwapForm] = useState({
    target_user_id: '',
    schedule_id: '',
    reason: ''
  });

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/login');
      return;
    }

    if (!user && location.state?.user) {
      setUser(location.state.user);
    }

    // Iter-88 · Admin Dashboard Restructuring — top-tier roles are always
    // redirected to Executive HQ. They can still opt-in to Staff view by
    // appending ?view=staff to the URL (used by the "View as Staff" button
    // on the Executive HQ page).
    const params = new URLSearchParams(window.location.search);
    const staffView = params.get('view') === 'staff';
    if (!staffView && (user?.role === 'admin' || user?.role === 'company_manager')) {
      navigate('/company-manager-dashboard');
      return;
    }

    const staffRoles = [
      'admin', 'company_manager', 'manager',
      'supervisor', 'crew_leader', 'agent',
    ];
    if (user?.role && !staffRoles.includes(user.role)) {
      if (user.role === 'tenant') navigate('/tenant-dashboard');
      else if (user.role === 'property_manager') navigate('/property-manager-dashboard');
    }
  }, [user, loading, navigate, location.state, setUser]);

  useEffect(() => {
    if (user && sessionVerified) {
      if (user.role !== 'manager') requestLocationPermission();
      fetchProperties();
      fetchVehicles();
      // Region catalog for the Incident/Roadside + Employee Field
      // Incident forms (leads only, but harmless to fetch for everyone).
      // Iter-171 · Phase 8 — regions endpoint lives under /routes.
      fetch(`${BACKEND_URL}/api/routes/regions`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => setRegions(Array.isArray(data) ? data : []))
        .catch(() => { /* noop */ });
      fetchMyViolations();
      fetchMySchedules();
      fetchAllSchedules();
      fetchSwapRequests();
      if (['company_manager','supervisor','crew_leader'].includes(user.role)) fetchWeeklyReport();
      fetchStaffGallery();
      fetchPickupStats();
      checkTodaySchedule();
      loadMyClockInRequest();
      // Iter-170 · Phase 2 — poll Live Messages unread count so the
      // sidebar badge stays fresh.
      const fetchLiveMsgUnread = () =>
        fetch(`${BACKEND_URL}/api/staff-messages/unread-count`, { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => { if (d) setLiveMsgUnread(d.total || 0); })
          .catch(() => {});
      fetchLiveMsgUnread();
      if (isLeadRole) {
        if (['company_manager','supervisor','crew_leader'].includes(user.role)) fetchDailyReports();
        fetchRegionalTeamView();
        fetchServicePlan();
        loadPendingClockIns();
        fetchTodayCrewAssignments();
      }
    }
  }, [user, sessionVerified]);

  // Poll clock-in status every 20s while a request is pending (agent side)
  // and refresh the approver queue on the same cadence (lead side).
  useEffect(() => {
    if (!user || !sessionVerified) return;
    const tick = () => {
      if (myClockInRequest?.status === 'pending') loadMyClockInRequest();
      if (isLeadRole) loadPendingClockIns();
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [user, sessionVerified, isLeadRole, myClockInRequest?.status]);

  const requestLocationPermission = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationPermission('granted');
          locationWarningShown.current = false;
        },
        (error) => {
          setLocationPermission('denied');
          if (!locationWarningShown.current) {
            locationWarningShown.current = true;
            toast.warning('Location is unavailable. Arrival verification needs location access; other records remain available.', { id: 'staff-location-permission' });
          }
        }
      );
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/properties`, { credentials:'include' });
      if (response.ok) {
        let data = await response.json();

        // Iter-77 — Scope enforcement. Crew Leaders + Site Agents only see
        // properties and cities that were explicitly assigned to them by
        // their Supervisor via the Hierarchy Assignments board. Admins,
        // company_managers, and supervisors keep full visibility for their
        // regional oversight role.
        if (['crew_leader', 'agent', 'site_agent', 'staff', 'driver'].includes(user?.role) && user?.user_id) {
          try {
            const asgRes = await fetch(
              `${BACKEND_URL}/api/company/staff-assignments`,
              { credentials: 'include' },
            );
            if (asgRes.ok) {
              const rows = await asgRes.json();
              const asg = Array.isArray(rows)
                ? rows.find((r) => r.user_id === user.user_id)
                : null;
              const allowedProps = new Set(asg?.assigned_properties || []);
              const allowedCities = new Set((asg?.assigned_cities || []).map((c) => c.toLowerCase()));
              if (allowedProps.size || allowedCities.size) {
                data = data.filter((p) => {
                  if (allowedProps.has(p.property_id)) return true;
                  if (allowedCities.size && allowedCities.has((p.city || '').toLowerCase())) return true;
                  return false;
                });
              }
            }
          } catch (_) { /* silent — leave data unfiltered on fetch failure */ }
        }

        setProperties(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch properties:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      // Section 4 (iter-134) — pull the richer procurement fleet list so
      // the expandable detail can render code, mileage, maintenance flag,
      // temp assignment, MPG anomaly, and gas card without a second call.
      const procResp = await fetch(`${BACKEND_URL}/api/procurement/vehicles`, { credentials: 'include' });
      if (procResp.ok) {
        const rich = await procResp.json();
        if (Array.isArray(rich)) {
          setVehicles(rich);
          return;
        }
      }
      if (!['admin','company_manager','supervisor','crew_leader'].includes(user?.role)) return;
      const response = await fetch(`${BACKEND_URL}/api/vehicles`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVehicles(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch vehicles:', error);
    }
  };

  const fetchMyViolations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/violations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMyViolations(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch violations:', error);
    }
  };

  const fetchMySchedules = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/schedules/my`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMySchedules(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch schedules:', error);
    }
  };

  const fetchAllSchedules = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/schedules`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAllSchedules(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch all schedules:', error);
    }
  };

  const fetchSwapRequests = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/swap-requests`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSwapRequests(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch swap requests:', error);
    }
  };

  const fetchWeeklyReport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/weekly-report`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWeeklyReport(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch weekly report:', error);
    }
  };

  const fetchStaffGallery = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/staff/gallery`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStaffGallery(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch staff gallery:', error);
    }
  };

  // Pull the Region → Properties → Staff matrix for lead roles. Drives the
  // "Regional Assignments" card on the Team tab and the assign-staff dialog.
  const fetchRegionalTeamView = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/routes/team-view`, { credentials: 'include' });
      if (res.ok) setRegionalTeamView(await res.json());
    } catch (e) {
      reportAccountFetchError('Failed to fetch regional team view', e);
    }
  };

  // Today's Service Plan — live snapshot of scheduled vs completed pickups
  // per region. Drives the "Today's Service Plan" card and at-risk badges.
  const fetchServicePlan = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/routes/todays-service-plan`, { credentials: 'include' });
      if (res.ok) setServicePlan(await res.json());
    } catch (e) {
      reportAccountFetchError('Failed to fetch service plan', e);
    }
  };

  // Stamp `region_code` on the picked staff IDs AND schedule them as a
  // discrete crew for today so the crew leader's dashboard picks them up
  // and Today's Daily Job section can filter to only confirmed leaders.
  // Manager / Supervisor only.
  const assignStaffToRegion = async (regionId, userIds) => {
    if (!regionId || !userIds.length) return toast.error('Pick a region and at least one agent.');
    try {
      const res = await fetch(`${BACKEND_URL}/api/routes/regions/${regionId}/assign-staff`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: userIds }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to confirm');
        return;
      }

      // ── Iter-77 · Create a crew_assignments doc so this confirmed
      // crew appears on the crew leader's dashboard and populates
      // Today's Daily Job Assignments section below. Fail-soft: the
      // region stamp is the primary side-effect, the crew doc is
      // additive scheduling. ────────────────────────────────────────
      const entry = regionalTeamView.find((e) => e.region.region_id === regionId);
      const picked = staffGallery.filter((s) => userIds.includes(s.user_id));
      const cl = picked.find((s) => s.role === 'crew_leader')
        || picked.find((s) => s.role === 'supervisor')
        || null;
      if (cl && entry) {
        const agentIds = picked.filter((s) => s.user_id !== cl.user_id).map((s) => s.user_id);
        const propertyIds = (entry.properties || []).map((p) => p.property_id);
        const cities = [...new Set((entry.properties || []).map((p) => (p.city || '').trim()).filter(Boolean))];
        try {
          const crewRes = await fetch(`${BACKEND_URL}/api/ops/crew-assignments`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              crew_leader_id: cl.user_id,
              agent_ids: agentIds,
              cities,
              property_ids: propertyIds,
              notes: `Confirmed via Regional Assignments · ${entry.region?.region_code || ''}`,
            }),
          });
          if (crewRes.ok) {
            await fetchTodayCrewAssignments();
          }
        } catch (_) { /* fail-soft */ }
      }

      toast.success(`Confirmed ${data.updated} agent${data.updated === 1 ? '' : 's'} · ${data.region_name || data.region_code}. Crew Leader dashboard updated.`);
      setStaffToAssignIds([]);
      setSelectedAssignRegion('');
      fetchRegionalTeamView();
      fetchStaffGallery();
    } catch (_) {
      toast.error('Network error');
    }
  };

  // Today's confirmed crews — used to filter the Daily Job Assignments
  // section to only crew leaders scheduled today.
  const fetchTodayCrewAssignments = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ops/crew-assignments/mine`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const today = new Date().toISOString().split('T')[0];
        const todaysCrews = (data.assignments || []).filter((a) => (a.created_at || '').startsWith(today));
        setTodayCrewLeaderIds(new Set(todaysCrews.map((a) => a.crew_leader_id).filter(Boolean)));
      }
    } catch (_) { /* silent */ }
  };

  // Iter-77 potential improvement · Clone the most recent (yesterday's or
  // earlier) crew composition for a region so a supervisor can stand up
  // the same team in one click. Runs on the client using
  // /api/ops/crew-assignments/mine as the ledger.
  const copyYesterdayCrew = async (regionId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ops/crew-assignments/mine`, { credentials: 'include' });
      if (!res.ok) { toast.error('Could not read your crew history'); return; }
      const data = await res.json();
      const today = new Date().toISOString().split('T')[0];
      const prior = (data.assignments || [])
        .filter((a) => a.region_id === regionId && !(a.created_at || '').startsWith(today))
        .sort((x, y) => (y.created_at || '').localeCompare(x.created_at || ''));
      const src = prior[0];
      if (!src) { toast.error('No previous crew to copy for this region.'); return; }

      const clonePayload = {
        crew_leader_id: src.crew_leader_id,
        agent_ids: src.agent_ids || [],
        cities: src.cities || [],
        property_ids: src.property_ids || [],
        notes: `Cloned from ${(src.created_at || '').slice(0, 10)} crew`,
      };
      const post = await fetch(`${BACKEND_URL}/api/ops/crew-assignments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clonePayload),
      });
      if (!post.ok) {
        const j = await post.json().catch(() => ({}));
        toast.error(j.detail || 'Clone failed');
        return;
      }
      toast.success(`Copied last crew (${(src.agent_ids || []).length + 1} members) · Crew Leader dashboard updated.`);
      await fetchTodayCrewAssignments();
    } catch (_) {
      toast.error('Network error');
    }
  };

  const fetchDailyReports = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/daily-reports`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMyDailyReports(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch daily reports:', error);
    }
  };

  const fetchPickupStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pickups/my-stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPickupStats({ today: data.today_pickups, weekly: data.weekly_pickups });
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch pickup stats:', error);
    }
  };

  // Agent-side: my clock-in request status (pending / approved / rejected)
  const loadMyClockInRequest = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/qr/clock-in/my-request`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setMyClockInRequest(data.pending || null);
      if (data.pending?.status === 'approved') setIsClockedIn(true);
    } catch (_e) { /* noop */ }
  };

  // Lead-side: pending clock-in queue (supervisors + crew leaders only)
  const loadPendingClockIns = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/qr/clock-in/pending`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setPendingClockIns(data.pending || []);
    } catch (_e) { /* noop */ }
  };

  const decidePendingClockIn = async (pendingId, action, notes = '') => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/qr/clock-in/pending/${pendingId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes })
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || `Failed to ${action}`);
        return;
      }
      toast.success(action === 'approve' ? 'Clock-in approved' : 'Clock-in rejected');
      loadPendingClockIns();
    } catch (_e) {
      toast.error(`Failed to ${action}`);
    }
  };

  const checkTodaySchedule = async () => {
    const today = new Date().toISOString().split('T')[0];
    const schedule = mySchedules.find(s => s.date === today);
    setTodaySchedule(schedule);
    if (schedule?.status === 'clocked_in') {
      setIsClockedIn(true);
    }
  };

  const fetchChatMessages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crew-chat`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setChatMessages(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch chat messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/crew-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage })
      });
      if (response.ok) {
        setNewMessage('');
        fetchChatMessages();
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  useEffect(() => {
    if (user && sessionVerified) {
      fetchChatMessages();
      const interval = setInterval(fetchChatMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [user, sessionVerified]);

  const fetchTrackingData = async (propertyId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/tracking`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTrackingData(data);
      }
    } catch (error) {
      reportAccountFetchError('Failed to fetch tracking data:', error);
    }
  };

  useEffect(() => {
    if (selectedTrackingProperty) {
      fetchTrackingData(selectedTrackingProperty);
      const interval = setInterval(() => fetchTrackingData(selectedTrackingProperty), 30000);
      return () => clearInterval(interval);
    }
  }, [selectedTrackingProperty]);

  useEffect(() => {
    if (mySchedules.length > 0) {
      checkTodaySchedule();
    }
  }, [mySchedules]);

  const handleClockIn = async () => {
    // Launch QR scanner; actual clock-in occurs after scanning the property QR.
    setQrScannerMode('clock-in');
  };

  const openPickupScanner = () => {
    if (!isClockedIn) {
      toast.error('Please clock in first before scanning unit QRs.');
      return;
    }
    setQrScannerMode('pickup');
  };

  const openBadgeScanner = () => {
    setQrScannerMode('badge');
  };

  const handleQRScanResult = async (qrData) => {
    if (!qrData) return;
    const mode = qrScannerMode;
    setQrScannerMode(null); // close scanner

    if (mode === 'clock-in') {
      try {
        const response = await fetch(`${BACKEND_URL}/api/qr/scan/clock-in`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ qr_data: qrData, location: currentLocation })
        });
        const data = await response.json();
        if (!response.ok) {
          toast.error(data.detail || 'Clock-in failed');
          return;
        }
        // Supervisor / crew leader → auto-approved. Everyone else → pending.
        if (data.status === 'approved') {
          setIsClockedIn(true);
          toast.success(`Clocked in at ${data.property_name}`);
          fetchMySchedules();
        } else {
          toast.info(`Clock-in submitted at ${data.property_name}. Waiting for supervisor / crew leader approval.`);
        }
        // Refresh the "my request" card so the pending banner appears immediately.
        loadMyClockInRequest();
      } catch (_e) {
        toast.error('Failed to clock in via QR');
      }
      return;
    }

    if (mode === 'pickup') {
      // Accept new UUID (NECS:U:) and legacy (NECS:UNIT:) formats.
      if (!qrData.startsWith('NECS:U:') && !qrData.startsWith('NECS:UNIT:')) {
        toast.error('That is not a unit QR code.');
        return;
      }
      setPickupContext({ qr_data: qrData });
      setPickupForm({ issue_type: 'none', notes: '', photos: [], actual_trash_bags: '', actual_recycling_bags: '', building_number: '' });
      setShowPickupForm(true);
      return;
    }

    if (mode === 'badge') {
      try {
        const response = await fetch(`${BACKEND_URL}/api/qr/scan/badge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ qr_data: qrData })
        });
        const data = await response.json();
        if (!response.ok) {
          toast.error(data.detail || 'Badge verification failed');
          return;
        }
        setBadgeResult(data);
        setShowBadgeResult(true);
      } catch (_e) {
        toast.error('Failed to verify badge');
      }
    }
  };

  const submitPickupScan = async (quick) => {
    if (!pickupContext) return;
    if (pickupForm.actual_trash_bags === '' || pickupForm.actual_recycling_bags === '') { toast.error('Record the actual trash and recycling bag counts, including zero.'); return; }
    const payload = {
      qr_data: pickupContext.qr_data,
      location: currentLocation,
      issue_type: quick ? 'none' : (pickupForm.issue_type || 'none'),
      notes: quick ? undefined : pickupForm.notes,
      photos: quick ? [] : pickupForm.photos,
      actual_trash_bags: Number(pickupForm.actual_trash_bags),
      actual_recycling_bags: Number(pickupForm.actual_recycling_bags),
      building_number: pickupForm.building_number || undefined,
    };
    try {
      const response = await fetch(`${BACKEND_URL}/api/qr/scan/pickup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.detail || 'Failed to log pickup');
        return;
      }
      toast.success(data.unit_number ? `Pickup logged for unit ${data.unit_number}` : 'Pickup logged');
      fetchPickupStats();
      setShowPickupForm(false);
      window.dispatchEvent(new CustomEvent('necs:collections-changed'));
      setPickupContext(null);
    } catch (_e) {
      toast.error('Failed to log pickup');
    }
  };

  const handleClockOut = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clock-out`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setIsClockedIn(false);
        toast.success('Clocked out successfully!');
        fetchMySchedules();
        // Master Architecture Feb 2026 — Performance Reward Loop pop-up.
        // Site Agents get the celebratory Good Job! summary after they
        // clock out (leads see the same card in the Team tab counters).
        if (['agent', 'site_agent', 'staff', 'crew_leader'].includes(user?.role)) {
          setGoodJobOpen(true);
        }
      }
    } catch (error) {
      toast.error('Failed to clock out');
    }
  };

  const handleAnalyzeImage = async (base64Image) => {
    setIsAnalyzingImage(true);
    toast.info('Analyzing image with AI...');
    try {
      // The base64 string might include the data URL prefix (e.g. data:image/jpeg;base64,).
      // The emergentintegrations library expects just the base64 part, but it usually handles the prefix.
      // We will pass the full string to the backend, which will pass it to the library.
      let imageData = base64Image;
      if (imageData.includes(',')) {
        imageData = imageData.split(',')[1];
      }

      const response = await fetch(`${BACKEND_URL}/api/violations/analyze-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_data: imageData })
      });

      if (response.ok) {
        const data = await response.json();
        setViolationForm(prev => ({
          ...prev,
          violation_type: data.violation_type,
          description: prev.description ? `${prev.description}\n\nAI Suggestion: ${data.description}` : data.description
        }));
        toast.success(`AI suggests: ${data.violation_type}`);
      } else {
        toast.error('AI analysis failed');
      }
    } catch (error) {
      toast.error('AI analysis error');
    } finally {
      setIsAnalyzingImage(false);
    }
  };

  const handleSubmitViolation = async (e) => {
    e.preventDefault();
    
    if (!violationForm.property_id || !violationForm.unit_number || !violationForm.violation_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/violations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...violationForm,
          location: currentLocation
        })
      });

      if (response.ok) {
        toast.success('Violation report submitted for review');
        setShowViolationDialog(false);
        setViolationForm({
          property_id: '',
          unit_number: '',
          building_number: '',
          violation_type: '',
          description: '',
          photos: []
        });
        fetchMyViolations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit violation');
      }
    } catch (error) {
      toast.error('Failed to submit violation report');
    }
  };

  const handleSubmitDailyReport = async (e) => {
    e.preventDefault();

    if (!dailyReportForm.property_id || !dailyReportForm.report_date || !dailyReportForm.hours_worked) {
      toast.error('Please complete date, hours, and property');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/daily-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...dailyReportForm,
          badge_number: user?.badge_number
        })
      });

      if (response.ok) {
        toast.success('Daily report submitted');
        setShowDailyReportDialog(false);
        setDailyReportForm({
          report_date: new Date().toISOString().split('T')[0],
          hours_worked: '',
          property_id: '',
          checklist: {
            trash_pickup: false,
            recycle_pickup: false,
            bulk_removal: false,
            power_wash: false,
            amenity_cleaned: false,
            violations_recorded: false,
            dumpsters_clean: false,
            common_area_trash_replaced: false
          },
          trash_photos: [],
          recycle_photos: [],
          common_area_photos: [],
          photos: [],
          notes: ''
        });
        fetchDailyReports();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit daily report');
      }
    } catch (error) {
      toast.error('Failed to submit daily report');
    }
  };

  const handleSubmitSafetyCheck = async (e) => {
    e.preventDefault();
    if (!safetyForm.check_type || !safetyForm.vehicle_id) {
      toast.error("Please select a check type and vehicle");
      return;
    }
    if (!safetyForm.fuel_level) {
      toast.error("Fuel level is required");
      return;
    }
    if (safetyForm.check_type === 'pre_shift' && (!safetyForm.has_license || !safetyForm.license_photo)) {
      toast.error("License verification is required for pre-shift checks");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicle-safety`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(safetyForm)
      });
      
      if (response.ok) {
        toast.success("Vehicle Safety Check submitted successfully!");
        setShowSafetyDialog(false);
        setSafetyStep(1);
        setSafetyForm({
          check_type: '',
          vehicle_id: '',
          vehicle_name: '',
          has_license: null,
          license_photo: null,
          fuel_level: '',
          gas_receipt_photo: null,
          checklist: {
            tires: false, windows: false, fuel: false, seats: false,
            lights_and_signals: false, brakes: false, horn: false, others: false
          },
          notes: '',
          vehicle_photos: []
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit safety check");
      }
    } catch (err) {
      toast.error("Failed to submit vehicle safety check");
    }
  };

  const handleSubmitLateNotification = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/late-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(lateForm)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.valid_notification) {
          toast.success('Late notification sent successfully');
        } else {
          toast.warning(data.note);
        }
        setShowLateNotification(false);
        setLateForm({ schedule_id: '', reason: '', estimated_arrival_time: '' });
      }
    } catch (error) {
      toast.error('Failed to send late notification');
    }
  };

  const handleRequestSwap = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/schedules/${swapForm.schedule_id}/swap-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          target_user_id: swapForm.target_user_id,
          schedule_id: swapForm.schedule_id,
          reason: swapForm.reason
        })
      });

      if (response.ok) {
        toast.success('Swap request sent!');
        setShowSwapRequest(false);
        setSwapForm({ target_user_id: '', schedule_id: '', reason: '' });
        fetchSwapRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to request swap');
      }
    } catch (error) {
      toast.error('Failed to request swap');
    }
  };

  const handleAcceptSwap = async (swapId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/swap-requests/${swapId}/accept`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Swap accepted! Awaiting supervisor approval.');
        fetchSwapRequests();
      }
    } catch (error) {
      toast.error('Failed to accept swap');
    }
  };

  const handleApproveSwapSupervisor = async (swapId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/swap-requests/${swapId}/approve-supervisor`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Swap approved! Awaiting manager final approval.');
        fetchSwapRequests();
      }
    } catch (error) {
      toast.error('Failed to approve swap');
    }
  };

  const handleRecordPickup = async (type) => {
    if (!todaySchedule) {
      toast.error('No schedule found for today');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/pickups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          property_id: todaySchedule.property_ids?.[0] || 'unknown',
          unit_number: 'batch',
          pickup_type: type,
          location: currentLocation
        })
      });

      if (response.ok) {
        toast.success(`${type === 'completed' ? 'Pickup' : 'Missed pickup'} recorded`);
        fetchPickupStats();
      }
    } catch (error) {
      toast.error('Failed to record pickup');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFD60A]"></div>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'supervisor': return { backgroundColor: '#7c3aed' };
      case 'crew_leader': return { backgroundColor: '#3b82f6' };
      default: return { backgroundColor: '#52525b' };
    }
  };

  const pendingReports = myViolations.filter(v => v.status === 'pending_company_review');
  const accountMenuItems = staffDashboardMenu(user, isLeadRole, liveMsgUnread, updatesUnread);
  const upcomingSchedules = mySchedules.filter(s => new Date(s.date) >= new Date());
  const pendingSwapsForMe = swapRequests.filter(s => s.target_user_id === user.user_id && s.status === 'pending_target');
  const pendingSwapsToApprove = swapRequests.filter(s => 
    s.status === 'pending_supervisor' && isLeadRole
  );

  return (
    <>
      {/* Google Play "Prominent Disclosure" — appears the first time any
          field-staff user opens the Staff dashboard, before any GPS
          permission is requested. See LocationDisclosureModal. */}
      {user.role !== 'manager' && <LocationDisclosureModal user={user} />}
      {/* Master Architecture Feb 2026 — CEO / Admin "Executive HQ" overlay.
          When opened, replaces the entire dashboard with the 12-department
          command center grid. Only rendered for admin / company_manager. */}
      {canAccessExecutiveHQ && executiveHQOpen && (
        <ExecutiveHQ
          user={user}
          onExit={() => setExecutiveHQOpen(false)}
          onJumpToOps={() => { setExecutiveHQOpen(false); toast.info('Switch to the Operations tab for that section.'); }}
        />
      )}
      {!(canAccessExecutiveHQ && executiveHQOpen) && (
    <DashboardShell
      menuItems={accountMenuItems}
      activeKey={typeof activeTab !== 'undefined' ? activeTab : null}
      onMenuClick={(item) => { if (typeof setActiveTab === 'function') setActiveTab(item.key); }}
      hideUserMenu
      topbarExtras={(
        <>
          {/* Language toggle — Site Agents only (per Feb 2026 UI directive).
              Supervisors + Crew Leaders + Drivers use English only. */}
          {user?.role === 'agent' && (
            <LanguageSwitcher variant="light" testIdPrefix="staff-lang" />
          )}
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:opacity-90 transition"
            style={{ backgroundColor: '#000000', color: '#C9A962', border: '1px solid #2D2D2D' }}
            aria-label="Sign out"
            data-testid="topbar-logout-button"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </>
      )}
    >
    <div className="min-h-full text-white" style={{ backgroundColor: '#2D2D2D' }} data-testid="staff-dashboard">
      <UpdatesBlockingGate />
      {/* Inner profile navbar — mirrors NECS Admin structure. Role pill on
          the left; single bell + profile avatar on the right. Gold bottom
          border aligns pixel-perfect (y=146) with the sidebar logo case. */}
      <nav className="sticky top-[49px] sm:top-[65px] z-20" style={{ backgroundColor: '#3D3D3D', borderBottom: '1px solid #C9A962' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20 relative gap-2">
            <div className="flex items-center space-x-3 mr-auto">
              <Badge style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }} data-testid="dashboard-role-badge">
                {(user?.role === 'supervisor' ? 'SUPERVISOR' :
                  user?.role === 'crew_leader' ? 'CREW LEADER' :
                  user?.role === 'manager' ? 'MANAGER' : 'STAFF')}
              </Badge>
              {locationPermission === 'granted' && (
                <div className="flex items-center space-x-2">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-400">GPS</span>
                </div>
              )}
            </div>
            {/* Iter-175 · Phase 2 — Trophy → Profile → Bell (right-
                aligned, tight cluster, 44×44 tap targets, badge inset).
                Trophy now visible for ALL staff roles including manager. */}
            <div className="flex items-center gap-1"
                 data-testid="staff-header-icon-cluster">
              <div className="min-w-[44px] min-h-[44px] flex items-center justify-center"
                   data-testid="topbar-trophy-wrap">
                <div data-testid="staff-trophy-wrap">
                  <ComplianceTrophy role={user?.role} roleType={user?.role_type} primaryService={user?.primary_service} />
                </div>
              </div>
              <div className="min-w-[44px] min-h-[44px] flex items-center justify-center"
                   data-testid="topbar-user-menu-wrap">
                <UserMenu />
              </div>
              <div className="min-w-[44px] min-h-[44px] flex items-center justify-center"
                   data-testid="topbar-bell-wrap">
                <NotificationsBell variant="dark" />
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <WelcomeStrip
          name={user?.name || 'Field Operations'}
          subtitle={`${user?.role?.replace('_', ' ').toUpperCase()}${user?.badge_number ? ` · ${user.badge_number}` : ''}${user?.region ? ` · ${user.region}` : ''}`}
          accent="#FFD60A"
          testid="staff-welcome-strip"
        />
        {/* Iter-92 · Field agent's current status shows immediately post-
            login so they see their standing on the account card the moment
            they land — same badge that lives in the header. */}
        {['agent', 'crew_leader', 'supervisor', 'manager'].includes((user?.role || '').toLowerCase()) && (
          <div className="mb-4 max-w-md" data-testid="staff-login-status-card">
            <FieldAgentStatusBadge variant="card" />
          </div>
        )}
        {/* iter-135 Section 4 — vehicle UI is consolidated inside the
            Fleet Status tab. No top-level MyAssignedVehicleCard here. */}
        <RegionScopeStrip className="mb-4" />
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <UserAvatar
              name={user.name}
              email={user.email}
              photo={user.profile_photo || user.picture}
              size="md"
              testid="staff-header-avatar"
            />
            <div className="flex-1">
              <h1 className="text-2xl font-bold" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                {user.name}
              </h1>
              <div className="flex items-center space-x-2">
                <Badge className={getRoleBadgeColor(user.role)}>
                  {user.role?.replace('_', ' ').toUpperCase()}
                </Badge>
                <Badge className="bg-zinc-800 text-[#FFD60A]">{user.badge_number}</Badge>
              </div>
            </div>
            {canAccessExecutiveHQ && (
              <Button
                onClick={() => { window.location.href = '/company-manager-dashboard'; }}
                className="bg-[#FFD60A] hover:bg-[#EAB308] text-black font-bold"
                data-testid="open-executive-hq-btn"
              >
                <Trophy className="h-4 w-4 mr-2" /> Executive HQ
              </Button>
            )}
          </div>
        </div>

        {/* Regional Manager top-of-dashboard hero was superseded iter-82 by
            the compact RegionalTeamBoardCard placed right below the Clock-In
            section (see below). */}

        {user?.role !== 'manager' && !isRegionalManagerView && (
          <Card className="overflow-hidden mb-6 border-2 border-[#C9A962] bg-zinc-900" data-testid="staff-service-banner">
          <img
            src="https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/z5gpw78x_1565.jpg"
            alt="NECS crew preparing service at Summit Business Center"
            className="w-full h-48 object-cover"
            data-testid="staff-service-banner-image"
          />
          <div className="p-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 55%, #2a2010 100%)' }}>
            <div className="absolute left-0 right-0 top-0 h-[2px]" style={{ background: 'linear-gradient(to right, transparent, #C9A962 30%, #FFD60A 50%, #C9A962 70%, transparent)' }} />
            <p
              className="text-[10px] uppercase tracking-[0.3em] mb-1.5"
              style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 600 }}
            >
              Noble Empire · Field Operations
            </p>
            <h2
              className="necs-dashboard-caption text-base sm:text-lg leading-snug"
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontStyle: 'italic',
                fontWeight: 600,
                color: '#ffffff',
                textShadow: '0 1px 6px rgba(0,0,0,0.5)',
              }}
              data-testid="staff-snapshot-tagline"
            >
              Setting the Standard for Property Care, Community Cleanliness &amp; Operational Excellence.
            </h2>
          </div>
        </Card>
        )}

        {/* Clock In/Out Card */}
        <Card className={`p-6 mb-6 border-2 ${isClockedIn ? 'bg-green-900/30 border-green-500' : 'bg-zinc-900 border-zinc-700'}`}>
          <div className="flex flex-wrap gap-3 justify-between items-center">
            <div>
              <h3 className="text-lg font-bold text-white">
                {isClockedIn ? 'Currently On Shift' : 'Not Clocked In'}
              </h3>
              {todaySchedule && (
                <p className="text-sm text-zinc-400">
                  Today: {SHIFTS[todaySchedule.shift_type]?.name} ({SHIFTS[todaySchedule.shift_type]?.start} - {SHIFTS[todaySchedule.shift_type]?.end})
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 min-w-0">
              {!isClockedIn ? (
                <Button 
                  onClick={handleClockIn}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={!todaySchedule}
                  data-testid="clock-in-btn"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Scan to Clock In
                </Button>
              ) : (
                <>
                  <Button 
                    onClick={openPickupScanner}
                    className="font-bold"
                    style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                    data-testid="scan-unit-btn"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Scan Unit
                  </Button>
                  <Button 
                    onClick={handleClockOut}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="clock-out-btn"
                  >
                    <Square className="h-4 w-4 mr-2" />
                    Clock Out
                  </Button>
                </>
              )}
              {isLeadRole && (
                <Button
                  onClick={openBadgeScanner}
                  variant="outline"
                  className="border-[#C9A962] text-[#C9A962]"
                  data-testid="verify-badge-btn"
                >
                  <Fingerprint className="h-4 w-4 mr-2" />
                  Verify Badge
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* iter-82 · Small dashboard placed right below Clock-In for the RM.
            Shows the uploaded NECS training-room "board" picture alongside a
            compact staff-roster preview so the manager lands with the team
            already in view. */}
        {isRegionalManagerView && <RegionalTeamBoardCard user={user} />}

        {/* Pending clock-in banner (agent side) */}
        {myClockInRequest && myClockInRequest.status === 'pending' && (
          <Card
            className="p-4 mb-6 border-2 border-amber-500 bg-amber-950/40"
            data-testid="my-pending-clock-in-banner"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-amber-300 font-bold text-sm">
                  Clock-in awaiting supervisor / crew leader approval
                </p>
                <p className="text-xs text-zinc-300 mt-0.5">
                  Scanned at <span className="font-semibold">{myClockInRequest.property_name}</span>
                  {' · '}
                  {myClockInRequest.scanned_at ? new Date(myClockInRequest.scanned_at).toLocaleTimeString() : ''}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-500 text-amber-300"
                onClick={loadMyClockInRequest}
                data-testid="refresh-my-pending-clock-in"
              >
                Refresh
              </Button>
            </div>
          </Card>
        )}
        {myClockInRequest && myClockInRequest.status === 'rejected' && (
          <Card className="p-4 mb-6 border-2 border-red-600 bg-red-950/30" data-testid="my-rejected-clock-in-banner">
            <p className="text-red-300 font-bold text-sm">
              Your clock-in was rejected by {myClockInRequest.rejected_by_name || 'your supervisor'}.
            </p>
            {myClockInRequest.decision_notes && (
              <p className="text-xs text-zinc-300 mt-1">Reason: {myClockInRequest.decision_notes}</p>
            )}
          </Card>
        )}

        {/* Pending clock-in approvals queue (supervisor / crew leader / manager) */}
        {isLeadRole && pendingClockIns.length > 0 && (
          <Card className="p-4 mb-6 border-2 border-[#C9A962] bg-zinc-900" data-testid="pending-clock-in-queue">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Fingerprint className="h-4 w-4 text-[#C9A962]" />
                  Clock-In Approvals
                </h3>
                <p className="text-xs text-zinc-400">Agents waiting for you to validate their arrival scan.</p>
              </div>
              <Badge className="bg-[#C9A962] text-black font-bold">{pendingClockIns.length}</Badge>
            </div>
            <div className="space-y-2">
              {pendingClockIns.map((p) => (
                <div
                  key={p.pending_id}
                  className="flex items-center justify-between gap-3 p-3 rounded-md bg-zinc-950 border border-zinc-800 flex-wrap"
                  data-testid={`pending-clock-in-${p.pending_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm">{p.user_name}</p>
                    <p className="text-xs text-zinc-400">
                      #{p.badge_number || '—'} · {p.property_name} · {new Date(p.scanned_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => decidePendingClockIn(p.pending_id, 'approve')}
                      data-testid={`approve-clock-in-${p.pending_id}`}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500 text-red-400"
                      onClick={() => decidePendingClockIn(p.pending_id, 'reject')}
                      data-testid={`reject-clock-in-${p.pending_id}`}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-zinc-900 border-l-4 border-l-[#FFD60A] p-4">
            <p className="text-2xl font-black text-[#FFD60A]">{pickupStats.today}</p>
            <p className="text-xs text-zinc-400">Today's Pickups</p>
          </Card>
          <Card className="bg-zinc-900 border-l-4 border-l-green-500 p-4">
            <p className="text-2xl font-black text-green-500">{pickupStats.weekly}</p>
            <p className="text-xs text-zinc-400">Weekly Pickups</p>
          </Card>
          <Card className="bg-zinc-900 border-l-4 border-l-amber-500 p-4">
            <p className="text-2xl font-black text-amber-500">{pendingReports.length}</p>
            <p className="text-xs text-zinc-400">Pending Reports</p>
          </Card>
          <Card className="bg-zinc-900 border-l-4 border-l-blue-500 p-4">
            <p className="text-2xl font-black text-blue-500">{upcomingSchedules.length}</p>
            <p className="text-xs text-zinc-400">Upcoming Shifts</p>
          </Card>
        </div>

        {/* QR Code for Supervisors/Crew Leaders */}
        {isLeadRole && user.qr_code && (
          <Card className="bg-zinc-900 border-2 border-[#FFD60A] p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white mb-1">Your Crew QR Code</h3>
                <p className="text-xs text-zinc-400">Agents scan this to clock in</p>
              </div>
              <img 
                src={`data:image/png;base64,${user.qr_code}`} 
                alt="Crew QR Code" 
                className="w-20 h-20"
              />
            </div>
          </Card>
        )}

        {/* Main Tabs — Iter-194 · Sidebar mirrors this exact order. */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <DashboardMenuTabs items={accountMenuItems} prefix="staff" />

          {/* Actions Tab */}
          <TabsContent value="actions" className="space-y-4">
            {/* Resident pickup queue — valet staff close the loop here. */}
            {['agent','crew_leader','supervisor','site_agent','staff','driver'].includes(user?.role) && <PickupQueueCard />}
            {/* S6 Phase 3 — field staff occupancy flagging (all field roles). */}
            <FlagUnitCard />
            {/* Supervisors — specialty requests routed to me by Ops.
                From here I fan out to my crew (agents / crew leaders)
                in the same region. Hidden for non-supervisor roles. */}
            {/* Specialty queue — iter-78 part 7 · mirror the supervisor
                menu into the RM so they see routed specialty requests too. */}
            {['supervisor', 'manager', 'company_manager', 'admin'].includes((user?.role || '').toLowerCase()) && <SupervisorSpecialtyQueue user={user} />}
            {(user?.role || '').toLowerCase() === 'agent' && <AgentJobCards />}
            {(user?.role || '').toLowerCase() === 'agent' && <MyDailyAssignments />}
            {(user?.role || '').toLowerCase() === 'agent' && <OrderedJobStepperCard />}
            {(user?.role || '').toLowerCase() === 'agent' && <DelegatedPickupEntry onLogged={fetchPickupStats} />}
            {/* Iter-92 · Service Summary — shift close-out for
                management-tier staff only (Iter-170 · Phase 1). Backend
                returns 403 for agents/crew_leaders. */}
            {['supervisor', 'manager', 'company_manager', 'admin', 'ceo',
              'regional_manager', 'director'].includes((user?.role || '').toLowerCase()) && (
              <ServiceSummaryWidget properties={properties || []} />
            )}
            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-4">
              <Dialog open={showViolationDialog} onOpenChange={setShowViolationDialog}>
                <DialogTrigger asChild>
                  <Card className="bg-[#FFD60A] p-6 cursor-pointer hover:bg-[#EAB308] transition-all" data-testid="report-violation-card">
                    <div className="flex items-center space-x-4">
                      <Camera className="h-8 w-8 text-black" />
                      <div>
                        <h3 className="text-xl font-bold text-black">Report Violation</h3>
                        <p className="text-sm text-black/70">Take photo evidence</p>
                      </div>
                    </div>
                  </Card>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 text-white border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Report Violation</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitViolation} className="space-y-4">
                    <div>
                      <Label>Property *</Label>
                      <Select value={violationForm.property_id} onValueChange={(v) => setViolationForm({...violationForm, property_id: v})}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Select property" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {properties.map((p) => (
                            <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Unit *</Label>
                        <Input value={violationForm.unit_number} onChange={(e) => setViolationForm({...violationForm, unit_number: e.target.value})} className="bg-zinc-800 border-zinc-700" required />
                      </div>
                      <div>
                        <Label>Building</Label>
                        <Input value={violationForm.building_number} onChange={(e) => setViolationForm({...violationForm, building_number: e.target.value})} className="bg-zinc-800 border-zinc-700" />
                      </div>
                    </div>
                    <div>
                      <Label>Type *</Label>
                      <Select value={violationForm.violation_type} onValueChange={(v) => setViolationForm({...violationForm, violation_type: v})}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="Overflowing trash">Overflowing trash</SelectItem>
                          <SelectItem value="No bag used">No bag used</SelectItem>
                          <SelectItem value="Trash outside can">Trash outside can</SelectItem>
                          <SelectItem value="Hazardous materials">Hazardous materials</SelectItem>
                          <SelectItem value="Missed pickup">Missed pickup</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={violationForm.description} onChange={(e) => setViolationForm({...violationForm, description: e.target.value})} className="bg-zinc-800 border-zinc-700" />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label>Photo Evidence</Label>
                        {violationForm.photos.length > 0 && (
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleAnalyzeImage(violationForm.photos[0].data)}
                            disabled={isAnalyzingImage}
                            className="bg-indigo-900/30 text-indigo-400 border-indigo-500 hover:bg-indigo-900/50"
                          >
                            {isAnalyzingImage ? 'Analyzing...' : 'AI Analyze Image'}
                          </Button>
                        )}
                      </div>
                      <PhotoCapture 
                        onPhotosChange={(photos) => {
                          setViolationForm({...violationForm, photos});
                        }} 
                        maxPhotos={3} 
                        existingPhotos={violationForm.photos} 
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold">
                      Submit Report
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={showLateNotification} onOpenChange={setShowLateNotification}>
                <DialogTrigger asChild>
                  <Card className="bg-amber-600 p-6 cursor-pointer hover:bg-amber-700 transition-all">
                    <div className="flex items-center space-x-4">
                      <Clock className="h-8 w-8 text-white" />
                      <div>
                        <h3 className="text-xl font-bold text-white">Notify Late</h3>
                        <p className="text-sm text-white/70">3+ hours before shift</p>
                      </div>
                    </div>
                  </Card>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 text-white border-zinc-800">
                  <DialogHeader>
                    <DialogTitle>Late Arrival Notification</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitLateNotification} className="space-y-4">
                    <div>
                      <Label>Schedule</Label>
                      <Select value={lateForm.schedule_id} onValueChange={(v) => setLateForm({...lateForm, schedule_id: v})}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Select schedule" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {upcomingSchedules.map((s) => (
                            <SelectItem key={s.schedule_id} value={s.schedule_id}>
                              {s.date} - {SHIFTS[s.shift_type]?.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Reason *</Label>
                      <Textarea value={lateForm.reason} onChange={(e) => setLateForm({...lateForm, reason: e.target.value})} className="bg-zinc-800 border-zinc-700" required />
                    </div>
                    <div>
                      <Label>Estimated Arrival</Label>
                      <Input type="time" value={lateForm.estimated_arrival_time} onChange={(e) => setLateForm({...lateForm, estimated_arrival_time: e.target.value})} className="bg-zinc-800 border-zinc-700" />
                    </div>
                    <div className="bg-amber-900/30 p-3 rounded text-sm">
                      <p className="text-amber-200">Must notify at least 3 hours before shift to avoid late penalty</p>
                    </div>
                    <Button type="submit" className="w-full bg-amber-600 text-white font-bold">
                      Send Notification
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {isLeadRole && (
                <Dialog open={showDailyReportDialog} onOpenChange={setShowDailyReportDialog}>
                  <DialogTrigger asChild>
                    <Card className="bg-blue-600 p-6 cursor-pointer hover:bg-blue-700 transition-all" data-testid="daily-report-card">
                      <div className="flex items-center space-x-4">
                        <ClipboardList className="h-8 w-8 text-white" />
                        <div>
                          <h3 className="text-xl font-bold text-white">Daily Report</h3>
                          <p className="text-sm text-white/70">Supervisor/Crew Leader checklist</p>
                        </div>
                      </div>
                    </Card>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 text-white border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="daily-report-dialog">
                    <DialogHeader>
                      <DialogTitle>Submit Daily Service Report</DialogTitle>
                    </DialogHeader>
                    <ServiceSignalsLegend
                      propertyIds={properties.map((p) => p.property_id)}
                      className="mb-2"
                    />
                    <form onSubmit={handleSubmitDailyReport} className="space-y-4">
                      <div>
                        <Label>Badge Number</Label>
                        <Input value={user?.badge_number || ''} readOnly className="bg-zinc-800 border-zinc-700" data-testid="daily-report-badge" />
                      </div>
                      <div>
                        <Label>Property *</Label>
                        <Select value={dailyReportForm.property_id} onValueChange={(value) => setDailyReportForm({ ...dailyReportForm, property_id: value })}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="daily-report-property-select">
                            <SelectValue placeholder="Select property" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {properties.map((p) => (
                              <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Date *</Label>
                          <Input
                            type="date"
                            value={dailyReportForm.report_date}
                            onChange={(e) => setDailyReportForm({ ...dailyReportForm, report_date: e.target.value })}
                            className="bg-zinc-800 border-zinc-700"
                            data-testid="daily-report-date"
                          />
                        </div>
                        <div>
                          <Label>Hours Worked *</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={dailyReportForm.hours_worked}
                            onChange={(e) => setDailyReportForm({ ...dailyReportForm, hours_worked: e.target.value })}
                            className="bg-zinc-800 border-zinc-700"
                            placeholder="e.g., 4"
                            data-testid="daily-report-hours"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Service Checklist</Label>
                        <div className="grid md:grid-cols-2 gap-2 mt-2">
                          {DAILY_REPORT_CHECKLIST.map((item) => (
                            <label key={item.key} className="flex items-center gap-2 text-sm text-zinc-300">
                              <input
                                type="checkbox"
                                checked={dailyReportForm.checklist[item.key]}
                                onChange={(e) => setDailyReportForm({
                                  ...dailyReportForm,
                                  checklist: { ...dailyReportForm.checklist, [item.key]: e.target.checked }
                                })}
                                className="h-4 w-4 accent-[#FFD60A]"
                                data-testid={`daily-report-checklist-${item.key}`}
                              />
                              {item.label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label>Notes</Label>
                        <Textarea
                          value={dailyReportForm.notes}
                          onChange={(e) => setDailyReportForm({ ...dailyReportForm, notes: e.target.value })}
                          className="bg-zinc-800 border-zinc-700"
                          placeholder="Other details for management"
                          data-testid="daily-report-notes"
                        />
                      </div>
                      {/* Iter-170 · Phase 3b — dedicated photo sections
                          per service type. Trash / Recycle / Common
                          Area load here. Other checked items require
                          ≥1 photo in the generic gallery below. */}
                      {dailyReportForm.checklist.trash_pickup && (
                        <div data-testid="daily-report-photos-trash-section">
                          <Label className="text-[#FFD60A]">Trash Pickup Photos *</Label>
                          <PhotoCapture
                            existingPhotos={dailyReportForm.trash_photos}
                            onPhotosChange={(ph) => setDailyReportForm({ ...dailyReportForm, trash_photos: ph })}
                            maxPhotos={5}
                            testIdPrefix="daily-report-trash"
                          />
                        </div>
                      )}
                      {dailyReportForm.checklist.recycle_pickup && (
                        <div data-testid="daily-report-photos-recycle-section">
                          <Label className="text-[#FFD60A]">Recycle Pickup Photos *</Label>
                          <PhotoCapture
                            existingPhotos={dailyReportForm.recycle_photos}
                            onPhotosChange={(ph) => setDailyReportForm({ ...dailyReportForm, recycle_photos: ph })}
                            maxPhotos={5}
                            testIdPrefix="daily-report-recycle"
                          />
                        </div>
                      )}
                      {dailyReportForm.checklist.common_area_trash_replaced && (
                        <div data-testid="daily-report-photos-common-section">
                          <Label className="text-[#FFD60A]">Common Area Photos *</Label>
                          <PhotoCapture
                            existingPhotos={dailyReportForm.common_area_photos}
                            onPhotosChange={(ph) => setDailyReportForm({ ...dailyReportForm, common_area_photos: ph })}
                            maxPhotos={5}
                            testIdPrefix="daily-report-common"
                          />
                        </div>
                      )}
                      {/* Generic gallery for every other service type. */}
                      {(dailyReportForm.checklist.bulk_removal
                        || dailyReportForm.checklist.power_wash
                        || dailyReportForm.checklist.amenity_cleaned
                        || dailyReportForm.checklist.violations_recorded
                        || dailyReportForm.checklist.dumpsters_clean) && (
                        <div data-testid="daily-report-photos-generic-section">
                          <Label className="text-[#FFD60A]">Other Service Photos *</Label>
                          <PhotoCapture
                            existingPhotos={dailyReportForm.photos}
                            onPhotosChange={(ph) => setDailyReportForm({ ...dailyReportForm, photos: ph })}
                            maxPhotos={8}
                            testIdPrefix="daily-report-generic"
                          />
                        </div>
                      )}
                      {(() => {
                        // Iter-170 · Phase 3b — submit disabled until
                        // every checked service has ≥1 photo in its
                        // appropriate bucket.
                        const c = dailyReportForm.checklist;
                        const needTrash = c.trash_pickup && (dailyReportForm.trash_photos || []).length === 0;
                        const needRecycle = c.recycle_pickup && (dailyReportForm.recycle_photos || []).length === 0;
                        const needCommon = c.common_area_trash_replaced && (dailyReportForm.common_area_photos || []).length === 0;
                        const otherChecked = c.bulk_removal || c.power_wash || c.amenity_cleaned
                                             || c.violations_recorded || c.dumpsters_clean;
                        const needGeneric = otherChecked && (dailyReportForm.photos || []).length === 0;
                        const anyChecked = c.trash_pickup || c.recycle_pickup || c.common_area_trash_replaced || otherChecked;
                        const disabled = !anyChecked || needTrash || needRecycle || needCommon || needGeneric;
                        return (
                          <>
                            {disabled && anyChecked && (
                              <p className="text-xs italic text-orange-300 text-center"
                                 data-testid="daily-report-submit-hint">
                                Attach at least one photo per checked service to enable submit.
                              </p>
                            )}
                            <Button type="submit" disabled={disabled}
                                    className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="daily-report-submit">
                              Submit Daily Report
                            </Button>
                          </>
                        );
                      })()}
                    </form>
                  </DialogContent>
                </Dialog>
              )}

              {isLeadRole && (
                <Dialog open={showSafetyDialog} onOpenChange={(open) => {
                  if(!open) {
                    setSafetyStep(1);
                    setSafetyForm({
                      check_type: '', vehicle_id: '', vehicle_name: '',
                      has_license: null, license_photo: null, fuel_level: '', gas_receipt_photo: null,
                      checklist: {tires: false, windows: false, fuel: false, seats: false, lights_and_signals: false, brakes: false, horn: false, others: false},
                      notes: '', vehicle_photos: []
                    });
                  }
                  setShowSafetyDialog(open);
                }}>
                  <DialogTrigger asChild>
                    <Card className="bg-teal-600 p-6 cursor-pointer hover:bg-teal-700 transition-all" data-testid="safety-check-card">
                      <div className="flex items-center space-x-4">
                        <Truck className="h-8 w-8 text-white" />
                        <div>
                          <h3 className="text-xl font-bold text-white">Vehicle Safety</h3>
                          <p className="text-sm text-white/70">Mandatory pre-shift check</p>
                        </div>
                      </div>
                    </Card>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 text-white border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Vehicle Safety Check</DialogTitle>
                    </DialogHeader>
                    
                    {safetyStep === 1 && (
                      <div className="space-y-6 py-4">
                        <div>
                          <Label>Inspection Type</Label>
                          <Select
                            value={safetyForm.check_type}
                            onValueChange={(val) => setSafetyForm({...safetyForm, check_type: val})}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-2">
                              <SelectValue placeholder="Select inspection type" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="pre_shift">Pre-Shift (Start of day)</SelectItem>
                              <SelectItem value="post_shift">Post-Shift (End of day)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Select Vehicle</Label>
                          <Select
                            value={safetyForm.vehicle_id}
                            onValueChange={(val) => {
                              const vehicle = vehicles.find(v => v.vehicle_id === val);
                              setSafetyForm({...safetyForm, vehicle_id: val, vehicle_name: vehicle?.make_model + ' (' + vehicle?.license_plate + ')'});
                            }}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-2">
                              <SelectValue placeholder="Select a vehicle" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {vehicles.filter(v => v.status !== 'non_operational').map(v => (
                                <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                                  {v.make_model} - {v.license_plate} ({v.status})
                                </SelectItem>
                              ))}
                              {vehicles.length === 0 && (
                                <SelectItem value="none" disabled>No vehicles available</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-zinc-400 mt-2">Note: Non-operational vehicles cannot be selected.</p>
                        </div>
                        
                        <div className="flex justify-end mt-8">
                          <Button 
                            className="bg-[#FFD60A] text-black hover:bg-[#EAB308]" 
                            disabled={!safetyForm.check_type || !safetyForm.vehicle_id}
                            onClick={() => {
                              if (safetyForm.check_type === 'post_shift') {
                                setSafetyStep(4);
                              } else {
                                setSafetyStep(2);
                              }
                            }}
                          >
                            Next <ChevronRight className="h-4 w-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {safetyStep === 2 && (
                      <div className="space-y-6 py-4">
                        <div className="text-center">
                          <h3 className="text-xl font-bold mb-4">Driver's License Check</h3>
                          <p className="text-zinc-400 mb-6">Do you have your valid Driver's License in your physical possession?</p>
                          
                          <div className="flex justify-center space-x-4">
                            <Button 
                              variant={safetyForm.has_license === true ? "default" : "outline"}
                              className={safetyForm.has_license === true ? "bg-green-600 text-white hover:bg-green-700" : "border-zinc-700 text-white hover:bg-zinc-800"}
                              onClick={() => setSafetyForm({...safetyForm, has_license: true})}
                            >
                              Yes, I have it
                            </Button>
                            <Button 
                              variant={safetyForm.has_license === false ? "default" : "outline"}
                              className={safetyForm.has_license === false ? "bg-red-600 text-white hover:bg-red-700" : "border-zinc-700 text-white hover:bg-zinc-800"}
                              onClick={() => setSafetyForm({...safetyForm, has_license: false})}
                            >
                              No, I don't
                            </Button>
                          </div>
                          
                          {safetyForm.has_license === false && (
                            <div className="mt-6 p-4 bg-red-900/30 border border-red-500 rounded text-red-400 text-sm">
                              <AlertTriangle className="h-5 w-5 inline mr-2" />
                              You cannot operate company vehicles without your Driver's License. Please retrieve it before starting your shift.
                            </div>
                          )}
                        </div>
                        <div className="flex justify-between mt-8">
                          <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800" onClick={() => setSafetyStep(1)}>
                            <ChevronLeft className="h-4 w-4 mr-2" /> Back
                          </Button>
                          <Button 
                            className="bg-[#FFD60A] text-black hover:bg-[#EAB308]" 
                            disabled={safetyForm.has_license !== true}
                            onClick={() => setSafetyStep(3)}
                          >
                            Next <ChevronRight className="h-4 w-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {safetyStep === 3 && (
                      <div className="space-y-6 py-4">
                        <div>
                          <h3 className="text-lg font-bold mb-2">Upload License Photo</h3>
                          <p className="text-sm text-zinc-400 mb-4">Please take a clear photo of your Driver's License for verification.</p>
                          <PhotoCapture 
                            onPhotosChange={(photos) => {
                              if (photos.length > 0) {
                                setSafetyForm({...safetyForm, license_photo: photos[0].data});
                              } else {
                                setSafetyForm({...safetyForm, license_photo: null});
                              }
                            }} 
                            maxPhotos={1} 
                            existingPhotos={safetyForm.license_photo ? [{ data: safetyForm.license_photo }] : []} 
                          />
                        </div>
                        <div className="flex justify-between mt-8">
                          <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800" onClick={() => setSafetyStep(2)}>
                            <ChevronLeft className="h-4 w-4 mr-2" /> Back
                          </Button>
                          <Button 
                            className="bg-[#FFD60A] text-black hover:bg-[#EAB308]" 
                            disabled={!safetyForm.license_photo}
                            onClick={() => setSafetyStep(4)}
                          >
                            Next <ChevronRight className="h-4 w-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {safetyStep === 4 && (
                      <form onSubmit={handleSubmitSafetyCheck} className="space-y-6 py-4">
                        <div>
                          <h3 className="text-lg font-bold mb-4">Vehicle Inspection</h3>
                          <div className="grid md:grid-cols-2 gap-3 mb-6">
                            {VEHICLE_CHECKLIST.map((item) => (
                              <label key={item.key} className="flex items-center gap-2 text-sm text-zinc-300 p-2 bg-zinc-800 rounded border border-zinc-700">
                                <input
                                  type="checkbox"
                                  checked={safetyForm.checklist[item.key]}
                                  onChange={(e) => setSafetyForm({
                                    ...safetyForm,
                                    checklist: { ...safetyForm.checklist, [item.key]: e.target.checked }
                                  })}
                                  className="h-4 w-4 accent-[#FFD60A]"
                                />
                                {item.label}
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <Label>Fuel Level *</Label>
                          <Select
                            value={safetyForm.fuel_level}
                            onValueChange={(val) => setSafetyForm({...safetyForm, fuel_level: val})}
                            required
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-2">
                              <SelectValue placeholder="Select fuel level" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              <SelectItem value="Full">Full</SelectItem>
                              <SelectItem value="3/4">3/4</SelectItem>
                              <SelectItem value="1/2">1/2</SelectItem>
                              <SelectItem value="1/4">1/4</SelectItem>
                              <SelectItem value="Empty">Empty (Needs Refuel)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label>Gas Receipt (If Refueled)</Label>
                          <div className="mt-2">
                            <PhotoCapture 
                              onPhotosChange={(photos) => {
                                if (photos.length > 0) {
                                  setSafetyForm({...safetyForm, gas_receipt_photo: photos[0].data});
                                } else {
                                  setSafetyForm({...safetyForm, gas_receipt_photo: null});
                                }
                              }} 
                              maxPhotos={1} 
                              existingPhotos={safetyForm.gas_receipt_photo ? [{ data: safetyForm.gas_receipt_photo }] : []} 
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label>Vehicle Details / Issues / Notes</Label>
                          <Textarea
                            value={safetyForm.notes}
                            onChange={(e) => setSafetyForm({ ...safetyForm, notes: e.target.value })}
                            className="bg-zinc-800 border-zinc-700 mt-2"
                            placeholder="Describe any issues, scratches, or missing items..."
                          />
                        </div>
                        
                        <div>
                          <Label>Photos of Incidents or Details (Optional)</Label>
                          <div className="mt-2">
                            <PhotoCapture 
                              onPhotosChange={(photos) => setSafetyForm({...safetyForm, vehicle_photos: photos})} 
                              maxPhotos={4} 
                              existingPhotos={safetyForm.vehicle_photos} 
                            />
                          </div>
                        </div>
                        
                        <div className="flex justify-between mt-8">
                          <Button type="button" variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800" onClick={() => {
                            if (safetyForm.check_type === 'post_shift') {
                              setSafetyStep(1);
                            } else {
                              setSafetyStep(3);
                            }
                          }}>
                            <ChevronLeft className="h-4 w-4 mr-2" /> Back
                          </Button>
                          <Button type="submit" className="bg-green-600 text-white hover:bg-green-700 font-bold">
                            Submit Inspection
                          </Button>
                        </div>
                      </form>
                    )}
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {/* Pending Swap Requests for Me */}
            {pendingSwapsForMe.length > 0 && (
              <Card className="bg-zinc-900 border-2 border-amber-500 p-4">
                <h3 className="font-bold text-white mb-3">Swap Requests for You</h3>
                {pendingSwapsForMe.map((swap) => (
                  <div key={swap.swap_id} className="flex justify-between items-center p-3 bg-zinc-800 rounded mb-2">
                    <div>
                      <p className="text-white">{swap.requester_name} wants to swap</p>
                      <p className="text-xs text-zinc-400">{swap.date} - {SHIFTS[swap.shift_type]?.name}</p>
                    </div>
                    <Button onClick={() => handleAcceptSwap(swap.swap_id)} className="bg-green-600 hover:bg-green-700">
                      Accept
                    </Button>
                  </div>
                ))}
              </Card>
            )}

            {/* Supervisor: Pending Approvals */}
            {pendingSwapsToApprove.length > 0 && (
              <Card className="bg-zinc-900 border-2 border-purple-500 p-4">
                <h3 className="font-bold text-white mb-3">Swaps Needing Your Approval</h3>
                {pendingSwapsToApprove.map((swap) => (
                  <div key={swap.swap_id} className="flex justify-between items-center p-3 bg-zinc-800 rounded mb-2">
                    <div>
                      <p className="text-white">{swap.requester_name} ↔ {swap.target_user_name}</p>
                      <p className="text-xs text-zinc-400">{swap.date}</p>
                    </div>
                    <Button onClick={() => handleApproveSwapSupervisor(swap.swap_id)} className="bg-purple-600 hover:bg-purple-700">
                      Approve
                    </Button>
                  </div>
                ))}
              </Card>
            )}
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-white">My Schedule</h2>
              <Dialog open={showSwapRequest} onOpenChange={setShowSwapRequest}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FFD60A] text-black hover:bg-[#EAB308]">
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Request Swap
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 text-white border-zinc-800">
                  <DialogHeader>
                    <DialogTitle>Request Shift Swap</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleRequestSwap} className="space-y-4">
                    <div>
                      <Label>My Shift to Swap</Label>
                      <Select value={swapForm.schedule_id} onValueChange={(v) => setSwapForm({...swapForm, schedule_id: v})}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Select your shift" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {upcomingSchedules.filter(s => !s.swap_requested).map((s) => (
                            <SelectItem key={s.schedule_id} value={s.schedule_id}>
                              {s.date} - {SHIFTS[s.shift_type]?.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Swap With</Label>
                      <Select value={swapForm.target_user_id} onValueChange={(v) => setSwapForm({...swapForm, target_user_id: v})}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Select colleague" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {staffGallery.filter(s => s.user_id !== user.user_id).map((s) => (
                            <SelectItem key={s.user_id} value={s.user_id}>
                              {s.name} ({s.badge_number})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Reason</Label>
                      <Textarea value={swapForm.reason} onChange={(e) => setSwapForm({...swapForm, reason: e.target.value})} className="bg-zinc-800 border-zinc-700" />
                    </div>
                    <div className="bg-zinc-800 p-3 rounded text-sm text-zinc-400">
                      Swap requests must be at least 1 day before the shift
                    </div>
                    <Button type="submit" className="w-full bg-[#FFD60A] text-black font-bold">
                      Send Request
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {mySchedules.length === 0 ? (
                <Card className="bg-zinc-900 p-8 text-center">
                  <Calendar className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No schedules assigned yet</p>
                </Card>
              ) : (
                mySchedules.map((schedule) => (
                  <Card key={schedule.schedule_id} className={`p-4 ${
                    schedule.status === 'completed' ? 'bg-green-900/30 border-green-600' :
                    schedule.is_late ? 'bg-red-900/30 border-red-600' :
                    'bg-zinc-900 border-zinc-700'
                  }`}>
                    <div className="flex justify-between items-center flex-wrap gap-2">
                      <div>
                        <p className="font-bold text-white">{schedule.date}</p>
                        <p className="text-sm text-zinc-400">
                          {SHIFTS[schedule.shift_type]?.name}: {SHIFTS[schedule.shift_type]?.start} - {SHIFTS[schedule.shift_type]?.end}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          schedule.status === 'completed' ? 'bg-green-500' :
                          schedule.status === 'clocked_in' ? 'bg-blue-500' :
                          schedule.swap_requested ? 'bg-amber-500' : 'bg-zinc-600'
                        }>
                          {schedule.swap_requested ? 'Swap Pending' : schedule.status}
                        </Badge>
                        {schedule.status !== 'completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10 text-xs h-7"
                            onClick={() => setScheduleRouteCtx({
                              subject: `Field-ops issue on schedule ${schedule.schedule_id} · ${schedule.date}`,
                              detail: `Shift: ${SHIFTS[schedule.shift_type]?.name || schedule.shift_type}\nStatus: ${schedule.status}\nAgent: ${user?.name}${user?.region ? ` · ${user.region}` : ''}${schedule.is_late ? '\n\nFlagged LATE.' : ''}`,
                              dept: schedule.is_late ? 'hr' : 'safety',
                              related: `Schedule ${schedule.schedule_id} · ${schedule.date}`,
                              priority: schedule.is_late ? 'high' : 'normal',
                            })}
                            data-testid={`staff-schedule-route-${schedule.schedule_id}`}
                          >
                            <ArrowRightLeft className="h-3 w-3 mr-1" /> Route to…
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-4">
            {user?.role === 'manager' && <RegionalDelegationWorkspace />}
            {user?.role !== 'manager' && <>
            {user?.role === 'supervisor' && <SupervisorSharedTeams />}
            {user?.role === 'supervisor' && <SupervisorChainBoards />}
            {user?.role === 'crew_leader' && <CrewLeaderChainBoards />}
            {/* Supervisor + Manager both get an emergency broadcast panel
                (iter-78 part 7 — mirror the supervisor menu into RM). */}
            {['supervisor', 'manager', 'company_manager', 'admin'].includes(user?.role) && (
              <EmergencyBroadcastPanel user={user} regions={[]} />
            )}
            {/* Today's Service Plan card removed (iter-78 part 5) — the RM
                now sees the 3-column Regional Command board directly under
                the Communities selector inside HierarchyAssignments, so
                the standalone morning-standup card is redundant. */}

            {/* Hierarchy Assignments — Supervisors get dual boards (Crew
                Leaders / Site Agents) assigning communities in their region;
                Crew Leaders get shift delegation (agents → buildings/units).
                Both leads see the Crew Leaderboard. */}
            {isLeadRole && null /* Today's Service Plan removed — iter-78 part 5 */}

            {/* Hierarchy Assignments — Supervisors get dual boards (Crew
                Leaders / Site Agents) assigning communities in their region;
                Crew Leaders get shift delegation (agents → buildings/units).
                Both leads see the Crew Leaderboard. */}
            {/* Regional Assignment card — supervisor sees what was delegated
                to them; manager/CEO sees their full region scope card. */}
            {['supervisor', 'manager', 'company_manager', 'admin'].includes(user?.role) && (
              <SupervisorRegionalAssignmentCard user={user} />
            )}
            {['supervisor', 'manager', 'company_manager', 'admin'].includes(user?.role) && <HierarchyAssignments user={user} />}
            {user?.role === 'crew_leader' && <CrewDelegation user={user} />}
            {['supervisor', 'crew_leader', 'manager', 'company_manager', 'admin'].includes(user?.role) && <CrewLeaderboard />}

            {/* Regional Assignment Cards — one card per direct-report tier
                scoped to the caller's own region only. RM sees one card per
                supervisor; supervisor sees one card per crew leader. Both
                render a Scoop chip strip for on-the-fly city filtering.
                Crew Leaders keep the legacy region matrix below. */}
            {['manager', 'company_manager', 'admin', 'supervisor'].includes(user?.role) && (
              <RegionalAssignmentCards user={user} />
            )}
            {/* Iter-78 pt 12 — live per-crew progress bars for Supervisor /
                RM / CEO oversight. Pulls /api/ops/crew-progress every 60s. */}
            {['supervisor', 'manager', 'company_manager', 'admin'].includes(user?.role) && (
              <SupervisorProgressBars user={user} />
            )}
            {/* Iter-141 · Section 6 — Agent View Preview (read-only mirror
                of the mobile field-agent execution flow). Supervisor +
                Ops leads only. */}
            {['supervisor', 'manager', 'company_manager', 'admin'].includes(user?.role) && (
              <SupervisorAgentViewPreview user={user} />
            )}
            {/* Nationwide Scalability Masterplan — Regional Heat Map + Ops
                Assets for RM / CEO. Crew Leaders get the Building Scheduler. */}
            {['manager', 'company_manager', 'admin', 'supervisor'].includes(user?.role) && (
              <RegionalHeatMap />
            )}
            {['manager', 'company_manager', 'admin'].includes(user?.role) && (
              <RegionalInventoryTab />
            )}
            {['crew_leader', 'supervisor'].includes(user?.role) && (
              <BuildingScheduler user={user} />
            )}
            {['crew_leader'].includes(user?.role) && isLeadRole && (
              <Card
                className="bg-zinc-900 border-2 p-6 mb-2"
                style={{ borderColor: 'rgba(201, 169, 98, 0.5)' }}
                data-testid="regional-assignments-card"
              >
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    <MapPin className="h-5 w-5 mr-2" style={{ color: '#C9A962' }} />
                    REGIONAL ASSIGNMENTS · {regionalTeamView.filter(e => e.region?.region_code).length} regions
                  </h3>
                  <span className="text-xs text-zinc-400">
                    Each community auto-snaps to its DFW region. Confirming an assignment routes today&apos;s crew to the crew leader&apos;s dashboard.
                  </span>
                </div>

                {regionalTeamView.length === 0 ? (
                  <p className="text-zinc-400 text-sm">No regions seeded yet. Ask the NECS Admin to click <strong style={{ color: '#C9A962' }}>Seed Texas Regions (DFW)</strong> in Operations &rarr; Routes &amp; Regions.</p>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="regional-assignments-grid">
                    {regionalTeamView.map((entry) => {
                      const r = entry.region;
                      const isUnassigned = r.region_id === '_unassigned';
                      return (
                        <Card
                          key={r.region_id}
                          className="bg-zinc-800 border-zinc-700 p-3"
                          data-testid={`region-card-${r.region_id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="min-w-0 flex items-center gap-2">
                              <span
                                className="h-3 w-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: r.color || '#888' }}
                              />
                              <div className="min-w-0">
                                <p className="font-bold text-white truncate text-sm">
                                  {r.region_code ? <span style={{ color: '#C9A962' }} className="mr-1">{r.region_code}</span> : null}
                                  {r.name}
                                </p>
                                {r.state ? <p className="text-[10px] text-zinc-400">{r.state}</p> : null}
                              </div>
                            </div>
                            <Badge className="bg-zinc-700 text-white text-[10px]">
                              {entry.properties.length} · {entry.staff.length}
                            </Badge>
                          </div>

                          {entry.properties.length > 0 && (
                            <div className="mb-2">
                              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Communities</p>
                              <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                                {entry.properties.slice(0, 8).map((p) => (
                                  <li
                                    key={p.property_id}
                                    className="text-xs text-zinc-300 truncate"
                                    data-testid={`region-prop-${p.property_id}`}
                                    title={p.address || p.property_name}
                                  >
                                    · {p.property_name}
                                    {p.city ? <span className="text-zinc-500"> — {p.city}</span> : null}
                                  </li>
                                ))}
                                {entry.properties.length > 8 && (
                                  <li className="text-[10px] text-zinc-500">+ {entry.properties.length - 8} more</li>
                                )}
                              </ul>
                            </div>
                          )}

                          {entry.staff.length > 0 && (
                            <div className="mb-2">
                              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Assigned Crew</p>
                              <div className="flex flex-wrap gap-1">
                                {entry.staff.slice(0, 6).map((s) => (
                                  <Badge
                                    key={s.user_id}
                                    className="text-[10px]"
                                    style={getRoleBadgeColor(s.role)}
                                    title={`${s.name} · ${s.role}${s.role_type ? ' · ' + s.role_type : ''}`}
                                    data-testid={`region-staff-${s.user_id}`}
                                  >
                                    {s.name?.split(' ')[0] || s.badge_number}
                                  </Badge>
                                ))}
                                {entry.staff.length > 6 && (
                                  <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">+{entry.staff.length - 6}</Badge>
                                )}
                              </div>
                            </div>
                          )}

                          {!isUnassigned && ['supervisor', 'manager'].includes(user.role) && (
                            <div className="space-y-1.5 mt-1">
                              <Button
                                size="sm"
                                className="w-full bg-[#C9A962] text-black hover:opacity-90 font-bold text-xs"
                                onClick={() => { setSelectedAssignRegion(r.region_id); setStaffToAssignIds([]); }}
                                data-testid={`region-assign-open-${r.region_id}`}
                              >
                                <Users className="h-3 w-3 mr-1" /> Confirm assignment
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full border-[#C9A962]/60 text-[#C9A962] hover:bg-[#C9A962]/10 font-bold text-[10px] uppercase tracking-widest"
                                onClick={() => copyYesterdayCrew(r.region_id)}
                                data-testid={`region-copy-yesterday-${r.region_id}`}
                              >
                                Copy last night&apos;s crew
                              </Button>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}

                {/* Assignment row — visible when a region is selected. */}
                {selectedAssignRegion && ['supervisor', 'manager'].includes(user.role) && (
                  <Card className="bg-zinc-900 border-2 border-[#C9A962] p-4 mt-4" data-testid="region-assign-dialog">
                    <p className="text-sm text-white mb-3 font-bold">
                      Stamp <span style={{ color: '#C9A962' }}>
                        {regionalTeamView.find((e) => e.region.region_id === selectedAssignRegion)?.region.name}
                      </span> onto these agents:
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3 max-h-48 overflow-y-auto">
                      {staffGallery
                        .filter((s) => ['supervisor', 'crew_leader', 'agent', 'site_agent', 'staff'].includes(s.role))
                        .map((s) => {
                          const checked = staffToAssignIds.includes(s.user_id);
                          return (
                            <label
                              key={s.user_id}
                              className={`flex items-center gap-2 p-2 rounded border text-xs cursor-pointer transition ${
                                checked ? 'border-[#C9A962] bg-zinc-800' : 'border-zinc-700 bg-zinc-900'
                              }`}
                              data-testid={`assign-pick-${s.user_id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  setStaffToAssignIds((ids) =>
                                    ids.includes(s.user_id)
                                      ? ids.filter((x) => x !== s.user_id)
                                      : [...ids, s.user_id],
                                  )
                                }
                              />
                              <div className="min-w-0 flex-1">
                                <p className="text-white truncate">{s.name}</p>
                                <p className="text-[10px] text-zinc-400">
                                  {s.role?.replace('_', ' ')}{s.region ? ` · ${s.region}` : ''}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-zinc-700 text-zinc-300"
                        onClick={() => { setSelectedAssignRegion(''); setStaffToAssignIds([]); }}
                        data-testid="region-assign-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[#C9A962] text-black hover:opacity-90 font-bold"
                        onClick={() => assignStaffToRegion(selectedAssignRegion, staffToAssignIds)}
                        data-testid="region-assign-submit"
                      >
                        Confirm assignment ({staffToAssignIds.length})
                      </Button>
                    </div>
                  </Card>
                )}
              </Card>
            )}

            {isLeadRole && (
              <Card className="bg-zinc-900 border-2 border-purple-500/50 p-6 mb-6" data-testid="todays-daily-job-card">
                <h3 className="font-bold text-lg mb-4 flex items-center text-white">
                  <Calendar className="h-5 w-5 text-purple-400 mr-2" />
                  Today&apos;s Daily Job Assignments
                  <Badge className="ml-2 bg-purple-500/20 border border-purple-500/50 text-purple-200 text-[10px]">
                    Crew Leaders scheduled today
                  </Badge>
                </h3>
                {(() => {
                  // Iter-77 · Source of truth = today's confirmed
                  // crew_assignments docs (todayCrewLeaderIds). We
                  // hydrate each leader from staffGallery / allSchedules
                  // so the row still renders even if the leader
                  // doesn't have a separate shift-schedule for today.
                  const today = new Date().toISOString().split('T')[0];
                  const leaders = [...todayCrewLeaderIds];
                  const rows = leaders.map((uid) => {
                    const s = allSchedules.find((x) => x.user_id === uid && x.date === today);
                    const g = staffGallery.find((x) => x.user_id === uid);
                    return {
                      key: s?.schedule_id || `crew-${uid}`,
                      user_id: uid,
                      user_name: s?.user_name || g?.name || 'Unknown Crew Leader',
                      badge_number: s?.badge_number || g?.badge_number || '',
                      shift_type: s?.shift_type || g?.assigned_shift || '',
                      status: s?.status || 'confirmed',
                      is_late: s?.is_late || false,
                      profile_photo: s?.profile_photo || g?.profile_photo || null,
                    };
                  });
                  return (
                    <div className="space-y-3">
                      {rows.length === 0 ? (
                        <p className="text-zinc-400 text-sm" data-testid="todays-daily-job-empty">
                          No crew leaders confirmed for today yet &mdash; confirm an assignment above.
                        </p>
                      ) : (
                        rows.map((sched) => (
                          <Card
                            key={sched.key}
                            className="bg-zinc-800 border-zinc-700 p-3 flex justify-between items-center"
                            data-testid={`todays-daily-job-row-${sched.user_id}`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
                                {sched.profile_photo ? (
                                  <img src={sched.profile_photo} alt={sched.user_name} className="w-full h-full object-cover" />
                                ) : (
                                  <User className="h-5 w-5 text-zinc-400" />
                                )}
                              </div>
                              <div>
                                <p className="font-bold text-white">{sched.user_name}</p>
                                <div className="flex space-x-2">
                                  {sched.badge_number && <Badge className="bg-zinc-700 text-xs">{sched.badge_number}</Badge>}
                                  {sched.shift_type && SHIFTS[sched.shift_type] && (
                                    <Badge className="bg-blue-500 text-xs">{SHIFTS[sched.shift_type]?.name}</Badge>
                                  )}
                                  <Badge className="bg-[#C9A962] text-black text-[10px] font-bold">CREW LEADER</Badge>
                                </div>
                              </div>
                            </div>
                            <Badge className={
                              sched.status === 'completed' ? 'bg-green-500' :
                              sched.status === 'clocked_in' ? 'bg-blue-500' :
                              sched.is_late ? 'bg-red-500' : 'bg-[#C9A962] text-black'
                            }>
                              {sched.is_late ? 'LATE' : (sched.status === 'confirmed' ? 'CONFIRMED' : (sched.status || 'Scheduled'))}
                            </Badge>
                          </Card>
                        ))
                      )}
                    </div>
                  );
                })()}
              </Card>
            )}

            {user.role === 'agent' && (
              <Card className="bg-zinc-900 border-2 border-blue-500/50 p-6 mb-6">
                <h3 className="font-bold text-lg mb-4 flex items-center text-white">
                  <Users className="h-5 w-5 text-blue-400 mr-2" />
                  Supervisor Contacts
                </h3>
                <p className="text-sm text-zinc-400 mb-4">For call-ins, call-offs, or sick leaves, please contact your supervisor immediately.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {staffGallery.filter(s => s.role === 'supervisor' || s.role === 'crew_leader').map(sup => (
                    <Card key={sup.user_id} className="bg-zinc-800 border-zinc-700 p-4 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
                          {sup.profile_photo ? (
                            <img src={sup.profile_photo} alt={sup.name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-6 w-6 text-zinc-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-white">{sup.name}</p>
                          <Badge className="bg-purple-500 text-xs mb-1">{sup.role.replace('_', ' ')}</Badge>
                          <p className="text-sm text-zinc-300">📞 {sup.phone || 'Phone not available'}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>
            )}

            <Card className="bg-zinc-900 p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center text-white">
                <Users className="h-5 w-5 text-[#FFD60A] mr-2" />
                Team Members
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                {staffGallery.map((staff) => (
                  <div key={staff.user_id} className="text-center">
                    <div className={`w-14 h-14 mx-auto rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden border-2 ${
                      staff.user_id === user.user_id ? 'border-[#FFD60A]' : 'border-zinc-700'
                    }`}>
                      {staff.profile_photo ? (
                        <img src={staff.profile_photo} alt={staff.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-7 w-7 text-zinc-500" />
                      )}
                    </div>
                    <p className="text-xs text-white mt-1 truncate">{staff.name}</p>
                    <Badge className={`text-xs ${getRoleBadgeColor(staff.role)}`}>
                      {staff.badge_number}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>

            {/* Weekly Leaderboard */}
            {weeklyReport?.leaderboard && (
              <Card className="bg-zinc-900 p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center text-white">
                  <Trophy className="h-5 w-5 text-[#FFD60A] mr-2" />
                  Weekly Leaderboard
                </h3>
                <div className="space-y-2">
                  {weeklyReport.leaderboard.slice(0, 5).map((agent, idx) => (
                    <div key={agent.user_id} className={`flex items-center p-2 rounded ${idx === 0 ? 'bg-amber-900/30' : 'bg-zinc-800'}`}>
                      <span className={`text-lg font-bold w-6 ${idx === 0 ? 'text-[#FFD60A]' : 'text-zinc-400'}`}>#{idx + 1}</span>
                      <div className="w-8 h-8 rounded-full bg-zinc-700 mx-2 flex items-center justify-center overflow-hidden">
                        {agent.profile_photo ? (
                          <img src={agent.profile_photo} alt={agent.user_name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-4 w-4 text-zinc-400" />
                        )}
                      </div>
                      <span className="flex-1 text-white text-sm">{agent.user_name}</span>
                      <span className="text-[#FFD60A] font-bold">{agent.pickup_count}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            </>}
          </TabsContent>

          {/* Live Tracking Tab */}
          {isLeadRole && (
            <TabsContent value="tracking" className="space-y-4">
              <DriverTrackingPanel />
              <Card className="bg-zinc-900 p-6 border-2 border-blue-500/30">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                  <div>
                    <h3 className="text-xl font-bold flex items-center text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                      <MapPin className="h-5 w-5 text-blue-400 mr-2" />
                      CREW LIVE TRACKING
                    </h3>
                    <p className="text-sm text-zinc-400">Monitor active routes and job assignments by property.</p>
                  </div>
                  <Select
                    value={selectedTrackingProperty}
                    onValueChange={setSelectedTrackingProperty}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-full md:w-64">
                      <SelectValue placeholder="Select Property" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {properties.map(p => (
                        <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {!selectedTrackingProperty ? (
                  <div className="text-center py-12">
                    <MapPin className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">Select a property to view active routes and crew positions</p>
                  </div>
                ) : !trackingData ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <p className="text-zinc-400 mt-2">Loading tracking data...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <Card className="bg-zinc-800 border-zinc-700 p-4">
                        <p className="text-xs text-zinc-400 mb-1">Total Units</p>
                        <p className="text-2xl font-black text-white">{trackingData.total_units}</p>
                      </Card>
                      <Card className="bg-zinc-800 border-zinc-700 p-4">
                        <p className="text-xs text-zinc-400 mb-1">Completed</p>
                        <p className="text-2xl font-black text-green-500">{trackingData.completed_today}</p>
                      </Card>
                      <Card className="bg-zinc-800 border-zinc-700 p-4">
                        <p className="text-xs text-zinc-400 mb-1">Active Routes</p>
                        <p className="text-2xl font-black text-blue-500">{trackingData.active_routes?.length || 0}</p>
                      </Card>
                    </div>

                    {trackingData.active_routes?.length === 0 ? (
                      <p className="text-zinc-400 text-center py-8">No active routes for this property today.</p>
                    ) : (
                      trackingData.active_routes?.map((route) => (
                        <Card key={route.route_id} className="bg-zinc-800 border-zinc-700 p-4">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="font-bold text-white flex items-center">
                                {route.driver_name}
                                {route.last_location && (
                                  <span className="ml-2 flex items-center">
                                    <MapPin className="h-4 w-4 text-green-500 animate-pulse" />
                                    <span className="text-xs text-green-400 ml-1">Live</span>
                                  </span>
                                )}
                              </h4>
                              <p className="text-xs text-zinc-400">{route.shift_time}</p>
                            </div>
                            <Badge className={route.status === 'completed' ? 'bg-green-600' : 'bg-blue-600'}>
                              {route.status}
                            </Badge>
                          </div>
                          
                          <div className="mb-4">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-zinc-400">Progress</span>
                              <span className="text-white font-bold">{route.completed_units.length} / {route.units.length}</span>
                            </div>
                            <div className="w-full bg-zinc-900 rounded-full h-2">
                              <div 
                                className="bg-[#FFD60A] h-2 rounded-full transition-all"
                                style={{width: `${(route.completed_units.length / route.units.length) * 100}%`}}
                              ></div>
                            </div>
                          </div>

                          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                            {route.units.map((unit) => {
                              const isCompleted = route.completed_units.includes(unit);
                              const hasViolation = route.violations?.some(v => v.unit_number === unit);
                              
                              return (
                                <div
                                  key={unit}
                                  className={`p-2 rounded text-center font-bold text-xs ${
                                    hasViolation ? 'bg-red-900/50 text-red-400 border border-red-500' :
                                    isCompleted ? 'bg-green-900/50 text-green-400 border border-green-500' :
                                    'bg-zinc-900 text-zinc-500 border border-zinc-700'
                                  }`}
                                >
                                  {unit}
                                </div>
                              );
                            })}
                          </div>
                        </Card>
                      ))
                    )}
                  </div>
                )}
              </Card>
            </TabsContent>
          )}

          {/* Iter-171 · Phase 1 — merged Messages tab hosts BOTH the
              existing crew chat AND the two-channel Live Messages
              panel so there is a single Messages surface. */}
          <TabsContent value="chat" className="space-y-4">
            <LiveMessagesPanel user={user} />
            <StaffChatTab
              user={user}
              chatMessages={chatMessages}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              onSendMessage={handleSendMessage}
            />
          </TabsContent>

          {/* Iter-178 · Phase 7 — Cleaners specialty services queue. */}
          <TabsContent value="specialty" className="space-y-4" data-testid="staff-content-specialty">
            <CleanerSpecialtyBoard user={user} />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <StaffReportsTab
              user={user}
              myDailyReports={myDailyReports}
              myViolations={myViolations}
              properties={properties}
              regions={regions}
              vehicles={vehicles}
            />
          </TabsContent>

          {/* Crew Assignments Tab — supervisors compose new job cards for
              their Crew Leaders; Crew Leaders reorder + dispatch agents;
              agents see their trimmed job cards. Reacts to ?tab=assignments
              in the URL when notifications route staff back here. */}
          <TabsContent value="assignments" className="space-y-4" data-testid="staff-assignments-content">
            <PropertyStructureBoard />
            <CrewAssignmentsTab
              user={user}
              regions={regions}
              properties={properties}
            />
          </TabsContent>

          {/* Contracts Tab — every property the contractor is responsible
              for, with the exact specialty services active on its
              service_policies contract. */}
          <TabsContent value="contracts" className="space-y-4" data-testid="staff-contracts-content">
            {properties.length === 0 ? (
              <Card className="bg-zinc-900 border border-[#C9A962]/40 p-6 text-center">
                <ClipboardList className="h-8 w-8 mx-auto text-zinc-600 mb-2" />
                <p className="text-white font-semibold">No properties assigned to you yet.</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Contracted services show up here once your supervisor assigns you a route.
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {properties.map((p) => (
                  <ContractedServicesCard
                    key={p.property_id}
                    property={p}
                    viewerRole="staff"
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Uber Trash — Job Controls */}
          <TabsContent value="uber-trash" className="space-y-4">
            <StaffJobControls user={user} />
          </TabsContent>
          {/* Iter-171 · Phase 1 — standalone Live Messages TabsContent
              removed. Both channels now live inside `chat` (Messages). */}

          {/* Iter-90 · Live Ops — Regional Manager view. Backend already
              scopes /api/operations/live-routes to the caller's region_id
              so this tab only shows this RM's regional routes + accuracy. */}
          {user?.role === 'manager' && (
            <TabsContent value="live-ops" className="space-y-4" data-testid="staff-live-ops-content">
              <RouteAlertsPanel />
              <LeaderboardCard department="valet" />
              <LiveRoutesMap />
              <AiAccuracyTrendCard />
              <AlertConfigCard canEdit={false} />
            </TabsContent>
          )}

          {/* Iter-78 pt 10 — Service History Summary. Daily job log for
              the caller with searchable date / day / month / year filter. */}
          <TabsContent value="history" className="space-y-4">
            <ServiceHistorySummary user={user} />
          </TabsContent>

          {/* Iter-194 · Field Staff Access Preview — read-only mirror
              of the property's gate + amenity codes so drivers /
              cleaners see access info before rolling on a job. */}
          <TabsContent value="access" className="space-y-4" data-testid="staff-access-content">
            <Card className="bg-zinc-900 p-4 border-2 border-[#FFD60A]/40">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-3">
                <div>
                  <h3 className="text-xl font-bold flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    <KeyRound className="h-5 w-5 text-[#FFD60A] mr-2" />
                    PROPERTY ACCESS &amp; ENTRY
                  </h3>
                  <p className="text-sm text-zinc-400">
                    Read-only. Pick a property to see its gate and amenity codes before you dispatch.
                  </p>
                </div>
                <Select
                  value={selectedAccessProperty}
                  onValueChange={setSelectedAccessProperty}
                >
                  <SelectTrigger
                    className="bg-zinc-800 border-zinc-700 text-white w-full md:w-72"
                    data-testid="staff-access-property-select"
                  >
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {properties.map((p) => (
                      <SelectItem key={p.property_id} value={p.property_id}>
                        {p.property_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedAccessProperty ? (
                <PropertyAccessEntryMirror propertyId={selectedAccessProperty} readOnly />
              ) : (
                <div className="text-center py-10 text-zinc-500 text-sm" data-testid="staff-access-empty">
                  Select a property above to preview its access codes.
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Attendance — every NECS employee gets a personal scorecard;
              Manager / Supervisor additionally get the full roster + log. */}
          <TabsContent value="attendance" className="space-y-4">
            <AttendanceTab user={user} />
          </TabsContent>

          {/* Updates & Notices — company update feed; existing Training
              modules preserved in the sub-tab. */}
          <TabsContent value="updates" className="space-y-4">
            <UpdatesNoticesPanel testIdPrefix="staff-updates"
              trainingSlot={<TrainingLauncher dataTestId="staff-training-launcher" />} />
          </TabsContent>

          {/* Section 5 (iter-137) + Iter-171 · Phase 3 — Operation Record
              now loads for Site Agent + Crew Leader + Supervisor +
              Regional Manager. Backend authorizes the extended set. */}
          {['agent', 'supervisor', 'crew_leader', 'manager', 'regional_manager'].includes((user?.role || '').toLowerCase()) && (
            <TabsContent value="record" className="space-y-4">
              <div className="flex flex-wrap gap-4"><a href="/shift-briefings" className="text-[#C9A962] underline" data-testid="staff-briefings-link">NECS Standard briefing</a>{['crew_leader','supervisor','manager','regional_manager'].includes(user?.role)&&<a href="/closeout-attention" className="text-amber-300 underline" data-testid="staff-attention-link">Overdue & returned work</a>}</div>
              <div className="flex flex-wrap gap-4"><a href="/operation-records" className="text-[#C9A962] underline" data-testid="staff-operation-closeout-link">Property assignment closeouts</a><a href="/staff-rewards" className="text-[#C9A962] underline" data-testid="staff-operation-progress-link">Monthly progress & six-month rewards</a></div>
              <div className="rounded-2xl p-5 max-w-xl" style={{ backgroundColor: '#141414' }} data-testid="staff-operation-record">
                <ResidentOperationRecord />
              </div>
            </TabsContent>
          )}

          {/* Fleet Status — Section 7 (iter-137). Shows the single vehicle
              assigned to the logged-in staff (Regional Manager /
              Supervisor / Crew Leader). Card mirrors the Procurement &
              Supply NECS admin fleet card fields exactly: vehicle name +
              code, operational status, inspection status, assigned
              driver, department, position, role, state. Expanded detail
              panel (Section 4 iter-135) remains intact inside the card. */}
          {isLeadRole && (
            <TabsContent value="fleet" className="space-y-4">
              <Card className="bg-zinc-900 border border-[#C9A962]/40 p-6" data-testid="staff-fleet-status">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-bold text-lg flex items-center text-white">
                    <Truck className="h-5 w-5 text-[#C9A962] mr-2" />
                    My Assigned Vehicle
                  </h3>
                </div>
                {(() => {
                  const uid = user?.user_id;
                  const today = new Date().toISOString().slice(0, 10);
                  const isMine = (v) => {
                    if (!uid) return false;
                    if (v?.assignment?.user_id === uid) return true;
                    const ta = v?.temp_assignment;
                    if (ta?.active && ta?.user_id === uid && ta.start_date <= today && ta.end_date >= today) return true;
                    return false;
                  };
                  const myVehicles = (vehicles || []).filter(isMine);
                  if (myVehicles.length === 0) {
                    return (
                      <p className="text-zinc-400 text-sm" data-testid="fleet-none-assigned">
                        No vehicle assigned to you yet. NECS Admin can assign one to your account from Procurement & Supply → Fleet.
                      </p>
                    );
                  }
                  return (
                    <div className="grid gap-3" data-testid="staff-fleet-grid">
                      {myVehicles.map((v) => {
                        const status = (v.status || 'operational').toLowerCase();
                        const statusColor = status === 'operational'
                          ? { bg: '#16a34a', label: 'OPERATIONAL' }
                          : status === 'maintenance'
                          ? { bg: '#ea580c', label: 'MAINTENANCE' }
                          : { bg: '#dc2626', label: 'NON-OPERATIONAL' };
                        const inspectionOk = !v.maintenance_flag?.active;
                        return (
                          <Card
                            key={v.vehicle_id}
                            className="bg-zinc-800 border-zinc-700 p-4 cursor-pointer hover:border-[#C9A962]/60 transition"
                            data-testid={`fleet-vehicle-${v.vehicle_id}`}
                            onClick={() => setFleetExpandedId((cur) => cur === v.vehicle_id ? null : v.vehicle_id)}
                          >
                            <div className="flex items-start justify-between mb-2 gap-3">
                              <div className="min-w-0">
                                <p className="font-bold text-white truncate" data-testid={`fleet-vehicle-name-${v.vehicle_id}`}>
                                  {v.make_model || v.vehicle_type || 'Vehicle'}
                                </p>
                                {v.vehicle_code && (
                                  <p className="text-[11px] text-[#FFD60A] font-mono" data-testid={`fleet-vehicle-code-${v.vehicle_id}`}>
                                    {v.vehicle_code}
                                  </p>
                                )}
                                <p className="text-xs text-zinc-500 font-mono">{v.license_plate || v.vin || '—'}</p>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <Badge
                                  className="text-[10px] font-bold whitespace-nowrap"
                                  style={{ backgroundColor: statusColor.bg, color: '#fff' }}
                                  data-testid={`fleet-status-${v.vehicle_id}`}
                                >
                                  {statusColor.label}
                                </Badge>
                                <Badge
                                  className="text-[10px] font-bold whitespace-nowrap"
                                  style={{ backgroundColor: inspectionOk ? '#0ea5e9' : '#dc2626', color: '#fff' }}
                                  data-testid={`fleet-inspection-${v.vehicle_id}`}
                                >
                                  {inspectionOk ? 'INSPECTION OK' : 'INSPECTION DUE'}
                                </Badge>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-[11px] mt-2" data-testid={`fleet-vehicle-meta-${v.vehicle_id}`}>
                              <MetaCell k="Driver" v={v.assignment?.name || v.driver_name || user?.name || '—'} />
                              <MetaCell k="Department" v={v.assignment?.department || user?.department || '—'} />
                              <MetaCell k="Position" v={v.assignment?.position || user?.position || '—'} />
                              <MetaCell k="Role" v={(v.assignment?.role || user?.role || '—').replace('_', ' ')} />
                              <MetaCell k="State" v={v.assignment?.state || user?.state || '—'} />
                              <MetaCell k="Region" v={v.assignment?.region || user?.region || '—'} />
                            </div>
                            {v.notes && (
                              <p className="text-xs text-zinc-400 mt-2 line-clamp-3">{v.notes}</p>
                            )}
                            {fleetExpandedId === v.vehicle_id && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <FleetVehicleDetail
                                  vehicle={v}
                                  user={user}
                                  onChanged={fetchVehicles}
                                />
                              </div>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  );
                })()}
                <p className="text-[11px] text-zinc-500 mt-4">
                  Read-only card mirrors the Procurement & Supply admin fleet layout. Click to open the full record, inspection, fuel receipt, MPG trend, and (for Regional Managers) temporary assignment controls.
                </p>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* QR Scanner */}
      <QRScanner
        open={qrScannerMode !== null}
        onOpenChange={(v) => { if (!v) setQrScannerMode(null); }}
        onScan={handleQRScanResult}
        title={
          qrScannerMode === 'clock-in' ? 'Scan Property QR to Clock In' :
          qrScannerMode === 'pickup' ? 'Scan Unit QR for Pickup' :
          qrScannerMode === 'badge' ? 'Scan Agent Badge QR' : 'Scan QR'
        }
        description={
          qrScannerMode === 'clock-in' ? 'Align the property entrance QR inside the frame.' :
          qrScannerMode === 'pickup' ? 'Scan the unit/door QR code to log this pickup.' :
          qrScannerMode === 'badge' ? 'Scan the agent badge to verify their identity.' : ''
        }
        dataTestId={`qr-scanner-${qrScannerMode || 'none'}`}
      />

      {/* Pickup form after unit scan */}
      <Dialog open={showPickupForm} onOpenChange={setShowPickupForm}>
        <DialogContent className="bg-[#2D2D2D] text-white border-[#C9A962] w-[calc(100%-2rem)] max-w-md max-h-[85dvh] overflow-y-auto" data-testid="pickup-record-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#C9A962]">
              {pickupContext?.unit_number ? `Unit ${pickupContext.unit_number}` : 'Log Pickup'}
            </DialogTitle>
            <DialogDescription className="text-zinc-300">Record observed quantities. Repeated scans do not add pickups.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300">Status</Label>
              <Select
                value={pickupForm.issue_type}
                onValueChange={(v) => setPickupForm({ ...pickupForm, issue_type: v, ...(['missed', 'no_placement'].includes(v) ? { actual_trash_bags: '0', actual_recycling_bags: '0' } : {}) })}
              >
                <SelectTrigger className="bg-[#3D3D3D] border-[#4a4a4a] text-white" data-testid="pickup-issue-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="pickup-outcome-completed">Pickup completed</SelectItem>
                  <SelectItem value="recycle" data-testid="pickup-outcome-recycle">Recycle pickup</SelectItem>
                  <SelectItem value="missed" data-testid="pickup-outcome-missed">Missed pickup · Orange</SelectItem>
                  <SelectItem value="no_placement" data-testid="pickup-outcome-no-placement">No placement · Orange</SelectItem>
                  <SelectItem value="violation" data-testid="pickup-outcome-violation">Violation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ActualBagCounts value={pickupForm} onChange={setPickupForm} disabled={['missed', 'no_placement'].includes(pickupForm.issue_type)} />
            <label className="block text-sm text-zinc-300">Building (required if unit numbers repeat)<input value={pickupForm.building_number} onChange={(e) => setPickupForm((v) => ({ ...v, building_number: e.target.value }))} className="w-full min-h-12 border border-zinc-500 rounded p-2 bg-zinc-900 text-white mt-1" data-testid="pickup-building-input" /></label>
            {!['none', 'recycle'].includes(pickupForm.issue_type) && (
              <>
                <div>
                  <Label className="text-zinc-300">Notes</Label>
                  <Textarea
                    value={pickupForm.notes}
                    onChange={(e) => setPickupForm({ ...pickupForm, notes: e.target.value })}
                    placeholder="Describe the issue..."
                    className="bg-[#3D3D3D] border-[#4a4a4a] text-white"
                    data-testid="pickup-notes-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 mb-2 block">Photos ({pickupForm.issue_type === 'violation' ? 'required' : 'optional'})</Label>
                  <PhotoCapture
                    existingPhotos={pickupForm.photos}
                    onPhotosChange={(photos) => setPickupForm({ ...pickupForm, photos })}
                    testIdPrefix="pickup-proof"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="border-zinc-600 text-zinc-300"
              onClick={() => { setShowPickupForm(false); setPickupContext(null); }}
              data-testid="pickup-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="font-bold"
              style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
              onClick={() => submitPickupScan(false)}
              disabled={pickupForm.actual_trash_bags === '' || pickupForm.actual_recycling_bags === '' || (pickupForm.issue_type === 'violation' && !pickupForm.photos.length)}
              data-testid="pickup-submit-btn"
            >
              Log Pickup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Badge verification result */}
      <Dialog open={showBadgeResult} onOpenChange={setShowBadgeResult}>
        <DialogContent className="bg-[#2D2D2D] text-white border-[#C9A962] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#C9A962]">Badge Verified</DialogTitle>
          </DialogHeader>
          {badgeResult && (
            <div className="flex flex-col items-center gap-3 py-2" data-testid="badge-verify-result">
              {badgeResult.profile_photo ? (
                <img
                  src={badgeResult.profile_photo.startsWith('data:') ? badgeResult.profile_photo : `data:image/jpeg;base64,${badgeResult.profile_photo}`}
                  alt={badgeResult.name}
                  className="w-24 h-24 rounded-full object-cover border-2 border-[#C9A962]"
                />
              ) : (
                <div className="w-24 h-24 rounded-full flex items-center justify-center bg-[#C9A962] text-[#2D2D2D] text-3xl font-bold">
                  {(badgeResult.name || '?').slice(0, 1)}
                </div>
              )}
              <div className="text-center">
                <p className="text-lg font-bold" data-testid="badge-verify-name">{badgeResult.name}</p>
                <p className="text-sm text-zinc-400">{badgeResult.role?.replace('_', ' ')}</p>
                <p className="text-sm text-[#C9A962] font-mono">{badgeResult.badge_number}</p>
                {badgeResult.is_blocked && (
                  <Badge className="bg-red-600 mt-2">BLOCKED</Badge>
                )}
              </div>
              {badgeResult.today_schedule ? (
                <div className="text-xs text-zinc-400 text-center">
                  Today: {badgeResult.today_schedule.shift_type} • {badgeResult.today_schedule.status}
                </div>
              ) : (
                <div className="text-xs text-amber-400">No schedule today</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Route-to-department dialog — lets field staff escalate a
          schedule issue (LATE, safety concern, missed pickup) directly
          to HR / Safety without leaving the schedule. */}
      <RouteTaskDialog
        open={!!scheduleRouteCtx}
        onClose={() => setScheduleRouteCtx(null)}
        defaultDept={scheduleRouteCtx?.dept || 'safety'}
        onSaved={() => setScheduleRouteCtx(null)}
        prefill={scheduleRouteCtx || undefined}
      />
      {/* Master Architecture Feb 2026 — Performance Reward Loop pop-up.
          Iter-78 pt 12: for site-agents / staff the modal opens in
          endShift mode with a "Confirm & Save" button that snapshots the
          shift; crew leaders still get the celebratory variant. */}
      <GoodJobDialog
        open={goodJobOpen}
        onClose={() => setGoodJobOpen(false)}
        userName={user?.name}
        endShift={['agent', 'site_agent', 'staff'].includes(user?.role)}
      />
    </div>
    </DashboardShell>
      )}
    </>
  );
}


/* Section 7 (iter-137) — Meta cell used inside the Fleet Status vehicle
 * card so the layout matches the Procurement admin fleet card format.
 */
function MetaCell({ k, v }) {
  return (
    <div className="min-w-0">
      <p className="text-[9px] uppercase tracking-widest text-zinc-500">{k}</p>
      <p className="text-[11px] text-white truncate">{v}</p>
    </div>
  );
}
